import { useEffect, useMemo, useState, useRef } from "react";
import { computeShotRecommendation } from "@/lib/shotMath";
import type {
  ShotInputs,
  ShotRecommendation,
  PlayerProfile,
  Club,
} from "@/lib/types";
import {
  loadPlayerProfile,
  savePlayerProfile,
  loadGreenLayout,
  loadGreenVectors,
  logShot,
  loadPinSheet,
  type PinSheetEntry,
} from "@/lib/data/firestoreDAL";
import {
  sampleVectorPath,
  puttLineBearingDeg,
  gpsOffsetToImageNorm,
  imageFracToXNorm,
  xNormToImageFrac,
  getTapImageMarginRatios,
  type GreenVector,
  type PuttPathSample,
} from "@/lib/greenVectors";
import { normalizeBag, DEFAULT_BAG } from "@/lib/bag";
import WindAnalytics from "@/components/WindAnalytics";
import {
  classifyRelativeWind,
  classifyWindBodyFrame,
  relativeWindDeg,
  relativeWindFromDeg,
  normalizeDeg360,
} from "@/lib/wind";
import WindCompassOverlay from "@/components/WindCompassOverlay";
import HelpTab from "@/components/HelpTab";
import PinSheetSetup from "@/components/PinSheetSetup";
import { resolveTargetCoordinates, TargetType, HOLE_TARGET_PRESETS, getHoleTargetPreset } from "@/lib/holeTargetPresets";
import { getAccuCurrentConditionsByLatLng } from "@/lib/weather";
import { getElevationDeltaFt } from "@/lib/elevation";
import { bearingDeg, haversineYards } from "@/lib/geo";
import { computeTargetRecommendation } from "@/lib/targeting";
import type { TargetRecommendation } from "@/lib/targeting";
import { computePuttBreakV3, computePuttBreakFromPath } from "@/lib/greenRead";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  computeFallLineBreak,
  computeFallLineCoordinateRead,
  type BallHeightVsHole,
  type FallLineSide,
} from "@/lib/fallLine";
import { log10 } from "@google-cloud/firestore/pipelines";
// Canonicalize player IDs so keys are stable across casing/whitespace
// Per-player localStorage key
const LS_PLAYER_ID = "sweetshot_playerId";
const canonId = (s: string) => (s ?? "").trim().toLowerCase();
const profileKey = (id: string) => `sweetshot_profile_${canonId(id)}`;

// Maximum GPS accuracy radius (meters) we will accept for yardage calculations.
// 8 m ≈ 9 yards — achievable on modern smartphones in open sky; 30 m was too
// loose (could span 2-3 clubs).
const GPS_ACCURACY_THRESHOLD_M = 8;

// -----------------------------------------------------------------------------
// Defaults & Firestore helpers
// -----------------------------------------------------------------------------
// We no longer use a second Firestore DB in the app.

export const DEFAULT_PROFILE: PlayerProfile = {
  id: "russell",
  name: "Russell",
  handicap: 15,
  handicapIndex: 14.1, // or whatever you want as default
  bag: DEFAULT_BAG,
};

// Firestore helpers for player profiles

// -----------------------------------------------------------------------------
// Shot logging helpers
// -----------------------------------------------------------------------------

// Simple numeric helper used in shot inputs
function toNumber(
  value: string | number | null | undefined,
  fallback = 0,
): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : fallback;
  }

  return fallback;
}

// -----------------------------------------------------------------------------
// Wind presets: direction relative to target line for current hole.
// 0   = tailwind (straight helping)
// 90  = right cross (blowing R → L)
// 180 = headwind (straight into)
// 270 = left cross (blowing L → R)
// -----------------------------------------------------------------------------

const LS_DEVICE_ID = "sweetshot_deviceId";
const LS_SESSION_ID = "sweetshot_sessionId";
const LS_SHOT_SEQ = "sweetshot_shotSeq";
const LS_TEXT_SCALE_PCT = "sweetshot_textScalePct";
const TEXT_SCALE_OPTIONS = [100, 115, 130] as const;

function getOrCreateId(lsKey: string) {
  try {
    const existing = localStorage.getItem(lsKey);
    if (existing) return existing;
    const fresh =
      (crypto as any)?.randomUUID?.() ??
      `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
    localStorage.setItem(lsKey, fresh);
    return fresh;
  } catch {
    return `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ShotPlanner: React.FC = () => {
  const displayNumber = (value: number | undefined | null) =>
    value === 0 || value === undefined || value === null ? "" : value;
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const saving = saveStatus === "saving";
  const [playerProfile, setPlayerProfile] =
    useState<PlayerProfile>(DEFAULT_PROFILE);
  const activePlayerIdRef = useRef<string>("");
  const gpsWatchIdRef = useRef<number | null>(null);
  const weatherPollLatRef = useRef<number | null>(null);
  const weatherPollLngRef = useRef<number | null>(null);
  const [currentBag, setCurrentBag] = useState<Club[]>(DEFAULT_BAG);
  const [targetType, setTargetType] = useState<TargetType>("pin");

  const [playerLat, setPlayerLat] = useState<number | null>(null);
  const [playerLng, setPlayerLng] = useState<number | null>(null);
  const [targetLat, setTargetLat] = useState<number | null>(null);
  const [targetLng, setTargetLng] = useState<number | null>(null);

  const [gpsStatus, setGpsStatus] = useState<string>("idle");
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [lastGpsFixAt, setLastGpsFixAt] = useState<number | null>(null);
  const [lastGpsAccuracyM, setLastGpsAccuracyM] = useState<number | null>(null);
  const [lastHoleChangedAt, setLastHoleChangedAt] = useState<number>(0);
  // Set whenever the player advances to a new putt (handleNextPutt) -- the
  // ball is known to have moved at that instant, so a GPS fix taken BEFORE
  // this timestamp must not be silently reused as the new putt's ball
  // position (see resolvedBallNorm). Confirmed on course: two consecutive
  // putts on the same hole produced byte-identical computed break/distance
  // because the second putt's read silently fell back to the same stale
  // GPS position from before the first putt, since the ball was never
  // re-tapped.
  const [lastPuttAdvancedAt, setLastPuttAdvancedAt] = useState<number>(0);
  const [weatherStatus, setWeatherStatus] = useState<string>("idle");
  const [weatherError, setWeatherError] = useState<string | null>(null);

  const [elevationStatus, setElevationStatus] = useState<string>("idle");
  const [elevationError, setElevationError] = useState<string | null>(null);
  const [windDirectionMode, setWindDirectionMode] = useState<"live" | "manual">(
    "live",
  );
  // Body-frame relative offset from shot direction: 0=headwind, 180=tailwind,
  // 90=right cross, 270=left cross (stored 0-315 to avoid negatives in select)
  const [manualRelativeWindBodyDeg, setManualRelativeWindBodyDeg] =
    useState<number>(0);

  const [puttDistanceFt, setPuttDistanceFt] = useState<number>(10);
  const [thetaStartDeg, setThetaStartDeg] = useState<number>(1.0);
  const [thetaMidDeg, setThetaMidDeg] = useState<number>(1.5);
  const [thetaApexDeg, setThetaApexDeg] = useState<number>(2.0);
  // Which putt this is on the current green — three-putts are the common
  // case, not the one-putt the tab used to assume. Resets whenever the
  // hole changes (see effect below).
  const [puttNumber, setPuttNumber] = useState<number>(1);
  // Per-point slope samples along the ball→pin line, from this green's
  // vectorized data (when it has any) — feeds computePuttBreakFromPath so
  // the read isn't reduced to one hand-picked fall line. Empty when the
  // green has no vector data or the ball isn't near it yet.
  const [vectorPath, setVectorPath] = useState<PuttPathSample[]>([]);
  const [vectorPathBearingDeg, setVectorPathBearingDeg] = useState<number>(0);

  // Putt-read feedback — mirrors the shot recommendation's execution/quality
  // rating, so the path-based read has real outcome data to check itself
  // against (and eventually tune from) instead of just the shot side.
  const [puttHoled, setPuttHoled] = useState<"yes" | "no" | null>(null);
  const [puttReadQuality, setPuttReadQuality] = useState<
    "agree" | "dont_agree" | "unclear" | null
  >(null);
  const [puttOutcomeStatus, setPuttOutcomeStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [puttOutcomeError, setPuttOutcomeError] = useState<string | null>(null);

  const [angleToFallLineDeg, setAngleToFallLineDeg] = useState<number>(90);
  const [sideOfFallLine, setSideOfFallLine] = useState<FallLineSide>("right");
  const [heightVsHole, setHeightVsHole] = useState<BallHeightVsHole>("above");
  const [angleToMTBDeg, setAngleToMTBDeg] = useState<number>(90);
  const [fallLineXFt, setFallLineXFt] = useState<number>(-10);
  const [fallLineYFt, setFallLineYFt] = useState<number>(-1);
  const [greenStimp, setGreenStimp] = useState<number>(10);
  const [rolloutFt, setRolloutFt] = useState<number>(1.5);
  const [mtbDeg, setMtbDeg] = useState<number>(0);
  const [playerId, setPlayerId] = useState<string>(() => {
    return canonId(localStorage.getItem(LS_PLAYER_ID) ?? "russell");
  });

  const [playerIdInput, setPlayerIdInput] = useState<string>(playerId);
  const [activeTab, setActiveTab] = useState<"shot" | "putt" | "profile" | "help">("shot");
  const [showPinSheetSetup, setShowPinSheetSetup] = useState(false);
  const [pinSheet, setPinSheet] = useState<Record<number, PinSheetEntry>>({});
  const deviceId = useMemo(() => getOrCreateId(LS_DEVICE_ID), []);
  const [sessionId, setSessionId] = useState<string>(() =>
    getOrCreateId(LS_SESSION_ID),
  );
  const [shotSeq, setShotSeq] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(LS_SHOT_SEQ) ?? "0") || 0;
    } catch {
      return 0;
    }
  });
  const [shotExecution, setShotExecution] = useState<
    "solid" | "cant_judge" | null
  >(null);
  const [recommendationQuality, setRecommendationQuality] = useState<
    "agree" | "dont_agree" | "unclear" | null
  >(null);
  const [outcomeStatus, setOutcomeStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [outcomeError, setOutcomeError] = useState<string | null>(null);
  const [lastShotOutcomeKey, setLastShotOutcomeKey] = useState<string | null>(
    null,
  );

  // Keep refs in sync so the weather poll interval always sees the latest coords.
  useEffect(() => {
    weatherPollLatRef.current = playerLat;
    weatherPollLngRef.current = playerLng;
  }, [playerLat, playerLng]);

  // Start continuous GPS watch on mount so positions arrive without cold-start latency.
  useEffect(() => {
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setPlayerLat(lat);
        setPlayerLng(lng);
        setGpsStatus("player-fixed");
        setLastGpsFixAt(Date.now());
        setLastGpsAccuracyM(
          typeof position.coords.accuracy === "number" ? position.coords.accuracy : null
        );
        console.log("[SS][gps watch]", {
          lat, lng, accuracyM: position.coords.accuracy, at: new Date().toISOString(),
        });
      },
      (error) => {
        console.warn("[SS][gps watch error]", error.message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );

    gpsWatchIdRef.current = watchId;
    setGpsStatus("locating");

    return () => {
      navigator.geolocation.clearWatch(watchId);
      gpsWatchIdRef.current = null;
    };
  }, []);

  // Auto-refresh wind speed and direction every 2 minutes so gusts are captured
  // without the player having to tap "Load weather" mid-round.
  useEffect(() => {
    const WIND_POLL_MS = 2 * 60 * 1000;

    const poll = async () => {
      const lat = weatherPollLatRef.current;
      const lng = weatherPollLngRef.current;
      if (lat == null || lng == null) return;
      try {
        const wx = await getAccuCurrentConditionsByLatLng(lat, lng);
        if (wx.windSpeedMph != null) handleShotInputChange("windSpeedMph", wx.windSpeedMph);
        if (wx.windGustMph != null) setWindGustMph(wx.windGustMph);
        if (wx.windDirectionDeg != null) handleShotInputChange("windDirectionDeg", wx.windDirectionDeg);
        if (wx.temperatureF != null) handleShotInputChange("temperatureF", wx.temperatureF);
        if (wx.humidityPct != null) handleShotInputChange("humidityPct", wx.humidityPct);
        console.log("[SS][weather poll] updated", { windSpeedMph: wx.windSpeedMph, windGustMph: wx.windGustMph, windDirectionDeg: wx.windDirectionDeg });
      } catch (err) {
        console.warn("[SS][weather poll] failed", err);
      }
    };

    const id = setInterval(poll, WIND_POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const id = canonId(playerId);
    activePlayerIdRef.current = id;

    (async () => {
      if (!id) return;

      // 1) local first
      let localFound = false;
      try {
        const raw = localStorage.getItem(profileKey(id));
        if (raw) {
          const local = JSON.parse(raw) as PlayerProfile;
          localFound = true;

          if (!cancelled) {
            setPlayerProfile({ ...local, id });
            setCurrentBag(normalizeBag(local.bag));
          }
        }
      } catch {}

      // 1b) if nothing local, seed immediately (prevents stale "Russell")
      if (!localFound && !cancelled) {
        const fresh: PlayerProfile = {
          ...DEFAULT_PROFILE,
          id,
          name: id,
          bag: DEFAULT_BAG,
        };
        setPlayerProfile(fresh);
        setCurrentBag(DEFAULT_BAG);

        try {
          localStorage.setItem(profileKey(id), JSON.stringify(fresh));
        } catch {}
      }

      // 2) cloud second (best effort) — ONLY if no local existed
      if (!localFound) {
        try {
          const cloud = await loadPlayerProfile(id);
          if (cancelled) return;
          if (activePlayerIdRef.current !== id) return;
          if (cloud) {
            setPlayerProfile(cloud);
            setCurrentBag(normalizeBag(cloud.bag));
            try {
              localStorage.setItem(profileKey(id), JSON.stringify(cloud));
            } catch {}
          }
        } catch {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [playerId]);
  const handleLoadSwitchPlayer = () => {
    const nextId = canonId(playerIdInput || "russell");
    setPlayerId(nextId);
    try {
      localStorage.setItem(LS_PLAYER_ID, nextId);
    } catch {}
  };

  const handleSaveProfileBag = async () => {
    const id = canonId(playerId ?? "");
    if (!id) {
      setSaveStatus("error");
      setSaveError("No playerId set.");
      return;
    }

    try {
      setSaveStatus("saving");
      setSaveError(null);

      const profileToSave: PlayerProfile = {
        ...playerProfile,
        id,
        bag: normalizeBag(currentBag),
      };

      // 1) always save locally first (authoritative for testing)
      try {
        localStorage.setItem(profileKey(id), JSON.stringify(profileToSave));
        localStorage.setItem(LS_PLAYER_ID, id);
      } catch {}

      // Make UI/state authoritative immediately after local save
      setPlayerId(id);
      setPlayerIdInput(id);
      setPlayerProfile(profileToSave);
      setCurrentBag(profileToSave.bag ?? []);

      // 2) best-effort cloud save (do NOT load here)
      let ok = false;
      try {
        ok = await savePlayerProfile(id, profileToSave);
      } catch {
        ok = false;
      }

      setSaveStatus("saved");
      window.setTimeout(() => setSaveStatus("idle"), 1200);

      if (!ok) setSaveError("Saved locally. Cloud sync unavailable (offline).");
      else setSaveError(null);
    } catch (error: any) {
      console.warn("[SS] Save failed", error?.code, error?.message, error);
      setSaveStatus("error");
      setSaveError(
        `${error?.code ?? "error"}: ${error?.message ?? "Save failed"}`,
      );
    }
  };

  // Shot recommendation returned from engine
  const [lastRec, setLastRec] = useState<ShotRecommendation | null>(null);
  const [targetRec, setTargetRec] = useState<TargetRecommendation | null>(null);
  const [pinLateralThird, setPinLateralThird] = useState<
    "left" | "center" | "right"
  >("center");
  const [pinDepthThird, setPinDepthThird] = useState<
    "front" | "middle" | "back"
  >("middle");

  const [leftPenalty, setLeftPenalty] = useState<"none" | "near" | "adjacent">(
    "none",
  );
  const [rightPenalty, setRightPenalty] = useState<
    "none" | "near" | "adjacent"
  >("none");
  const [frontPenalty, setFrontPenalty] = useState<
    "none" | "near" | "adjacent"
  >("none");
  const [backPenalty, setBackPenalty] = useState<"none" | "near" | "adjacent">(
    "none",
  );

  // Hole selection + green layout UI state
  const [hole, setHole] = useState<number>(1);
  const [greenLayoutUrl, setGreenLayoutUrl] = useState<string | null>(null);
  // Pixel-exact crop matching the vectorizer's own coordinate frame — used
  // as the tap target for ball/pin placement when available, since tapping
  // against the full-page greenLayoutUrl photo doesn't correspond at all to
  // where greenVectors' x/y actually live (see firestore-rules commit notes
  // and the Brentwood hole 1 investigation). Falls back to greenLayoutUrl
  // for holes/courses without this calibration yet.
  const [vectorTapImageUrl, setVectorTapImageUrl] = useState<string | null>(null);
  // Per-hole real-world calibration for the vectorTapImageUrl frame (see
  // GreenLayout type in firestoreDAL.ts). null when not yet calibrated for
  // this hole — callers fall back to holeAzimuthDeg + the 45ft default.
  const [vectorFrameCalibration, setVectorFrameCalibration] = useState<{
    imageUpTrueBearingDeg: number;
    greenHalfWidthFt: number;
    greenHalfDepthFt: number;
  } | null>(null);
  // Per-axis ratio of vectorTapImageUrl's padded half-dimension to the
  // green's true half-dimension (see getTapImageMarginRatios). Defaults to
  // {x:1,y:1} — no margin — until a hole's layout loads.
  const [tapImageMarginRatios, setTapImageMarginRatios] = useState<{ x: number; y: number }>({
    x: 1,
    y: 1,
  });
  const [greenLayoutLoading, setGreenLayoutLoading] = useState<boolean>(false);
  const [greenLayoutError, setGreenLayoutError] = useState<string | null>(null);
  const [holeAzimuthDeg, setHoleAzimuthDeg] = useState<number>(0);
  const [shotAzimuthDeg, setShotAzimuthDeg] = useState<number | null>(null);
  const effectiveShotAzimuthDeg = shotAzimuthDeg ?? holeAzimuthDeg;
  const [slopeDirection, setSlopeDirection] = useState<
    "level" | "uphill" | "downhill"
  >("level");
  const [slopeMagnitude, setSlopeMagnitude] = useState<number>(0);
  // "gps" = came from OpenTopoData, "fallback" = API failed (terrain not flat)
  const [elevationSource, setElevationSource] = useState<"gps" | "fallback" | null>(null);
  const [elevationDeltaFt, setElevationDeltaFt] = useState<number | null>(null);
  const [windGustMph, setWindGustMph] = useState<number | null>(null);
  const [distanceToGreenCenterYds, setDistanceToGreenCenterYds] = useState<number | null>(null);
  const [isNearGreen, setIsNearGreen] = useState<boolean>(false);
  const [isOnGreenLikely, setIsOnGreenLikely] = useState<boolean>(false);
  const [autoAdvanceHole, setAutoAdvanceHole] = useState<boolean>(true);
  const [autoAdvanceNotice, setAutoAdvanceNotice] = useState<string | null>(null);
  const wasNearCurrentGreenRef = useRef<boolean>(false);
  const [greenVectors, setGreenVectors] = useState<GreenVector[] | null>(null);
  // Tapped pin position on green image (normalized 0-1); null = use pin sheet / 9-zone fallback
  const [tappedPinXNorm, setTappedPinXNorm] = useState<number | null>(null);
  const [tappedPinYNorm, setTappedPinYNorm] = useState<number | null>(null);
  // Tapped ball position on green image (normalized 0-1); null = use GPS
  const [tappedBallXNorm, setTappedBallXNorm] = useState<number | null>(null);
  const [tappedBallYNorm, setTappedBallYNorm] = useState<number | null>(null);
  // When the ball was last tapped — used to auto-clear a stale tap (e.g. the
  // browser tab was left open since a previous day's round) so an old read
  // doesn't linger and get mistaken for today's.
  const lastBallTapAtRef = useRef<number>(0);
  // Only asked for when no pin position is already known (no pin sheet entry, no tap yet)
  const [greenTapMode, setGreenTapMode] = useState<"pin" | "ball">("pin");

  // Course id for green images
  const [courseId, setCourseId] = useState<string>(() => {
    const saved = localStorage.getItem("courseId")?.trim();

    if (!saved) return "BrentwoodCC";

    const normalized = saved.toLowerCase();
    if (normalized === "brentwood" || normalized === "brentwoodcc") {
      return "BrentwoodCC";
    }

    return saved;
  });

  useEffect(() => {
    localStorage.setItem("courseId", courseId);
  }, [courseId]);

  // App-wide text size preference (accessibility — Tailwind's text-* sizes
  // are rem-based, so scaling the root font-size proportionally resizes
  // everything without touching individual components).
  const [textScalePct, setTextScalePct] = useState<number>(() => {
    const saved = Number(localStorage.getItem(LS_TEXT_SCALE_PCT));
    return (TEXT_SCALE_OPTIONS as readonly number[]).includes(saved) ? saved : 100;
  });

  useEffect(() => {
    localStorage.setItem(LS_TEXT_SCALE_PCT, String(textScalePct));
    document.documentElement.style.fontSize = `${textScalePct}%`;
  }, [textScalePct]);

  // Load today's pin sheet whenever the course changes, and again after the
  // pre-round setup screen closes so newly-tapped pins take effect right away.
  useEffect(() => {
    let cancelled = false;
    loadPinSheet(courseId).then((result) => {
      if (!cancelled) setPinSheet(result);
    });
    return () => { cancelled = true; };
  }, [courseId, showPinSheetSetup]);

  const fallLineRead = useMemo(() => {
    return computeFallLineBreak({
      sideOfFallLine,
      heightVsHole,
      angleToFallLineDeg,
      localSlopeDeg: thetaMidDeg,
      mtbStrengthDeg: mtbDeg,
    });
  }, [sideOfFallLine, heightVsHole, angleToFallLineDeg, thetaMidDeg, mtbDeg]);
  
  const fallLineCoordinateRead = useMemo(() => {
    return computeFallLineCoordinateRead({
      xFromFallLineFt: fallLineXFt,
      yFromHoleFt: fallLineYFt,
      localSlopeDeg: thetaMidDeg,
      mtbStrengthDeg: mtbDeg,
    });
  }, [fallLineXFt, fallLineYFt, thetaMidDeg, mtbDeg]);
  // A path-based read needs at least one sample with real direction data —
  // "ocr_only" points (a printed number with no nearby arrow to infer a
  // direction from) don't carry enough to place it on the break/grade axes.
  const usableVectorPath = useMemo(
    () => vectorPath.filter((p) => p.directionDeg != null),
    [vectorPath]
  );

  const puttRead = useMemo(() => {
    if (usableVectorPath.length > 0) {
      return computePuttBreakFromPath({
        distanceFt: puttDistanceFt,
        path: vectorPath,
        puttLineBearingDeg: vectorPathBearingDeg,
        stimp: greenStimp,
        rolloutFt,
        green: {
          courseId,
          hole,
          mtbDirectionDeg: 0,
          mtbStrengthDeg: mtbDeg,
          stimpDefault: greenStimp,
        },
      });
    }

    return computePuttBreakV3({
      distanceFt: puttDistanceFt,
      slopes: {
        thetaStartDeg,
        thetaHoleDeg: thetaMidDeg,
        thetaMaxBreakDeg: thetaApexDeg,
      },
      sideOfFallLine: sideOfFallLine === "right" ? "RIGHT_OF_FALL_LINE" : "LEFT_OF_FALL_LINE",
      relationToFallLine: "ACROSS",
      puttDirectionDeg: undefined,
      stimp: greenStimp,
      rolloutFt,
      green: {
        courseId,
        hole,
        mtbDirectionDeg: 0,
        mtbStrengthDeg: mtbDeg,
        stimpDefault: greenStimp,
      },
    });
  }, [
    usableVectorPath,
    vectorPath,
    vectorPathBearingDeg,
    puttDistanceFt,
    thetaStartDeg,
    thetaMidDeg,
    thetaApexDeg,
    sideOfFallLine,
    greenStimp,
    rolloutFt,
    mtbDeg,
    courseId,
    hole,
  ]);

  // Clear a stale ball tap when returning to the Putt tab — otherwise a
  // browser tab left open since a previous day's round keeps showing that
  // old read until a fresh tap happens to overwrite it.
  const STALE_BALL_TAP_MS = 4 * 60 * 60 * 1000;
  useEffect(() => {
    if (activeTab !== "putt") return;
    if (tappedBallXNorm === null) return;
    if (Date.now() - lastBallTapAtRef.current > STALE_BALL_TAP_MS) {
      setTappedBallXNorm(null);
      setTappedBallYNorm(null);
    }
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;

    // Seed synchronously from the surveyed preset the instant the hole
    // changes — otherwise holeAzimuthDeg keeps the PREVIOUS hole's value
    // until the Firestore fetch below resolves. On adjacent holes that run
    // opposite directions (e.g. 11 and 12), that window was long enough for
    // the wind bucket to briefly reuse the old hole's classification. Also
    // prefer this over whatever's stored in Firestore's greenLayout doc,
    // which was never updated when the presets file got corrected to real
    // GPS-surveyed bearings.
    const presetAzimuth = getHoleTargetPreset(courseId, hole)?.holeAzimuthDeg;
    setHoleAzimuthDeg(
      typeof presetAzimuth === "number" && Number.isFinite(presetAzimuth) ? presetAzimuth : 0,
    );

    (async () => {
      setGreenLayoutLoading(true);
      setGreenLayoutError(null);

      try {
        const layout = await loadGreenLayout(courseId, hole);

        if (cancelled) return;

        if (!layout) {
          setGreenLayoutUrl(null);
          setVectorTapImageUrl(null);
          setVectorFrameCalibration(null);
          setTapImageMarginRatios({ x: 1, y: 1 });
          return;
        }

        setGreenLayoutUrl(layout.imageUrl ?? null);
        setVectorTapImageUrl(layout.vectorTapImageUrl ?? null);
        setVectorFrameCalibration(
          layout.imageUpTrueBearingDeg != null &&
            layout.greenHalfWidthFt != null &&
            layout.greenHalfDepthFt != null
            ? {
                imageUpTrueBearingDeg: layout.imageUpTrueBearingDeg,
                greenHalfWidthFt: layout.greenHalfWidthFt,
                greenHalfDepthFt: layout.greenHalfDepthFt,
              }
            : null
        );
        setTapImageMarginRatios(getTapImageMarginRatios(layout));

        if (presetAzimuth == null) {
          const az = layout.holeAzimuthDeg;
          setHoleAzimuthDeg(
            typeof az === "number" && Number.isFinite(az) ? az : 0,
          );
        }
      } catch (error: any) {
        setGreenLayoutError(
          error?.code ?? error?.message ?? "Failed to load green layout",
        );
        setGreenLayoutUrl(null);
      } finally {
        if (!cancelled) setGreenLayoutLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courseId, hole]);

  // Load slope vectors for the current hole from Firestore
  useEffect(() => {
    let cancelled = false;
    setGreenVectors(null);
    setTappedPinXNorm(null);
    setTappedPinYNorm(null);
    setTappedBallXNorm(null);
    setTappedBallYNorm(null);
    setGreenTapMode("pin");
    // Clear synchronously, not just implicitly via the effect below no
    // longer updating it — otherwise the previous hole's path lingers
    // (and keeps driving puttRead) until this hole's vectors load.
    setVectorPath([]);
    setVectorPathBearingDeg(0);

    loadGreenVectors(courseId, hole).then((vecs) => {
      if (!cancelled) {
        setGreenVectors(vecs);
        console.log("[SS][greenVectors] loaded", { hole, count: vecs?.length ?? 0 });
      }
    });

    return () => { cancelled = true; };
  }, [courseId, hole]);

  // Resolved in the vectorizer's own coordinate frame (matches greenVectors'
  // x/y and vectorTapImageUrl) — shared between the auto-populate effect
  // below and the on-image pin marker, so both agree on where the pin is.
  const activeHolePreset = useMemo(
    () => (HOLE_TARGET_PRESETS as any[]).find((p) => p.courseId === courseId && p.hole === hole),
    [courseId, hole]
  );

  // True once a real pin position is known (tapped this session, or set
  // ahead of the round via the pin sheet) — as opposed to the crude 9-zone
  // guess, which is the only case manual fall-line/elevation entry is
  // actually needed for.
  const pinKnown = tappedPinXNorm !== null || !!pinSheet[hole];

  const resolvedPinNorm = useMemo((): { xNorm: number; yNorm: number } | null => {
    // approachAzimuth = real tee->green bearing, used to rotate anything
    // defined relative to the golfer's approach (depth/lateral thirds) into
    // true north/east. imageFrameAzimuth/halfWidth/halfDepth are what
    // actually matter for landing in the right spot on vectorTapImageUrl —
    // use the measured per-hole calibration when we have it, since the
    // tap-crop's own orientation/size don't generally match holeAzimuthDeg
    // + an assumed 90x90ft box (confirmed wrong on Brentwood hole 16).
    const approachAzimuth = activeHolePreset?.holeAzimuthDeg ?? holeAzimuthDeg ?? 0;
    const imageFrameAzimuth = vectorFrameCalibration?.imageUpTrueBearingDeg ?? approachAzimuth;
    const halfWidthFt = vectorFrameCalibration?.greenHalfWidthFt;
    const halfDepthFt = vectorFrameCalibration?.greenHalfDepthFt;

    if (tappedPinXNorm !== null && tappedPinYNorm !== null) {
      return { xNorm: tappedPinXNorm, yNorm: tappedPinYNorm };
    }
    const sheetEntry = pinSheet[hole];
    if (sheetEntry?.lat != null && sheetEntry?.lng != null && activeHolePreset?.greenCenter) {
      const greenCenter = activeHolePreset.greenCenter;
      const FEET_PER_DEG_LAT = 364000;
      const FEET_PER_DEG_LNG = Math.cos((greenCenter.lat * Math.PI) / 180) * 364000;
      const ftNorthPin = (sheetEntry.lat - greenCenter.lat) * FEET_PER_DEG_LAT;
      const ftEastPin = (sheetEntry.lng - greenCenter.lng) * FEET_PER_DEG_LNG;
      return gpsOffsetToImageNorm({
        ftNorth: ftNorthPin, ftEast: ftEastPin, holeAzimuthDeg: imageFrameAzimuth,
        greenHalfWidthFt: halfWidthFt, greenHalfDepthFt: halfDepthFt,
      });
    }
    const depthFt =
      pinDepthThird === "front" ? 15 : pinDepthThird === "back" ? -15 : 0;
    const lateralFt =
      pinLateralThird === "left" ? -12 : pinLateralThird === "right" ? 12 : 0;
    const azRad = (approachAzimuth * Math.PI) / 180;
    const ftNorthPin = -depthFt * Math.cos(azRad) + lateralFt * (-Math.sin(azRad));
    const ftEastPin = -depthFt * Math.sin(azRad) + lateralFt * Math.cos(azRad);
    return gpsOffsetToImageNorm({
      ftNorth: ftNorthPin, ftEast: ftEastPin, holeAzimuthDeg: imageFrameAzimuth,
      greenHalfWidthFt: halfWidthFt, greenHalfDepthFt: halfDepthFt,
    });
  }, [tappedPinXNorm, tappedPinYNorm, pinSheet, hole, activeHolePreset, holeAzimuthDeg, pinDepthThird, pinLateralThird, vectorFrameCalibration]);

  const resolvedBallNorm = useMemo((): { xNorm: number; yNorm: number } | null => {
    if (tappedBallXNorm !== null && tappedBallYNorm !== null) {
      return { xNorm: tappedBallXNorm, yNorm: tappedBallYNorm };
    }
    // The GPS fallback below must not reuse a fix taken before this putt
    // (or this hole) started — the ball has definitely moved since then,
    // so a stale fix is worse than no fallback at all (matches gpsFixFresh's
    // pattern below, inlined here since that const isn't defined yet at
    // this point in the component).
    const ballGpsFixFresh =
      lastGpsFixAt != null &&
      lastGpsFixAt > lastPuttAdvancedAt &&
      lastGpsFixAt > lastHoleChangedAt &&
      Date.now() - lastGpsFixAt <= 30_000 &&
      (lastGpsAccuracyM == null || lastGpsAccuracyM <= GPS_ACCURACY_THRESHOLD_M);
    if (isOnGreenLikely && ballGpsFixFresh && playerLat != null && playerLng != null && activeHolePreset?.greenCenter) {
      const approachAzimuth = activeHolePreset?.holeAzimuthDeg ?? holeAzimuthDeg ?? 0;
      const imageFrameAzimuth = vectorFrameCalibration?.imageUpTrueBearingDeg ?? approachAzimuth;
      const greenCenter = activeHolePreset.greenCenter;
      const FEET_PER_DEG_LAT = 364000;
      const FEET_PER_DEG_LNG = Math.cos((greenCenter.lat * Math.PI) / 180) * 364000;
      const ftNorthBall = (playerLat - greenCenter.lat) * FEET_PER_DEG_LAT;
      const ftEastBall = (playerLng - greenCenter.lng) * FEET_PER_DEG_LNG;
      return gpsOffsetToImageNorm({
        ftNorth: ftNorthBall,
        ftEast: ftEastBall,
        holeAzimuthDeg: imageFrameAzimuth,
        greenHalfWidthFt: vectorFrameCalibration?.greenHalfWidthFt,
        greenHalfDepthFt: vectorFrameCalibration?.greenHalfDepthFt,
      });
    }
    return null;
  }, [
    tappedBallXNorm,
    tappedBallYNorm,
    isOnGreenLikely,
    playerLat,
    playerLng,
    activeHolePreset,
    holeAzimuthDeg,
    vectorFrameCalibration,
    lastGpsFixAt,
    lastPuttAdvancedAt,
    lastHoleChangedAt,
    lastGpsAccuracyM,
  ]);

  // Ball-to-pin distance measured directly from the two tapped/resolved
  // positions on the green image, converted to feet via the real per-hole
  // green size — NOT from raw phone GPS (autoTargetYardage below), whose
  // ~10-20ft+ consumer accuracy swamps a putt-length distance (confirmed on
  // course: GPS showed 33ft for an actual 15ft putt). GPS distance is fine
  // for a full approach shot from well off the green; it's the wrong tool
  // once both points are known precisely from the diagram/pin sheet instead.
  const puttBallToPinDistanceFt = useMemo((): number | null => {
    if (!resolvedBallNorm || !resolvedPinNorm) return null;
    const halfWidthFt = vectorFrameCalibration?.greenHalfWidthFt ?? 45;
    const halfDepthFt = vectorFrameCalibration?.greenHalfDepthFt ?? 45;
    const dxFt = (resolvedBallNorm.xNorm - resolvedPinNorm.xNorm) * 2 * halfWidthFt;
    const dyFt = (resolvedBallNorm.yNorm - resolvedPinNorm.yNorm) * 2 * halfDepthFt;
    return Math.sqrt(dxFt * dxFt + dyFt * dyFt);
  }, [resolvedBallNorm, resolvedPinNorm, vectorFrameCalibration]);

  // Auto-populate slope inputs from the vector field along the ball→pin line.
  // Ball position prefers a tap on the vector-aligned green image (works
  // anywhere, any time) and otherwise needs GPS to confirm we're actually on
  // the green. Pin position comes from resolvedPinNorm above.
  useEffect(() => {
    if (!greenVectors || !greenVectors.length) return;

    const ball = resolvedBallNorm;
    if (!ball) return;

    const pin = resolvedPinNorm;
    if (!pin) return;

    const path = sampleVectorPath({
      vectors: greenVectors,
      ballXNorm: ball.xNorm,
      ballYNorm: ball.yNorm,
      pinXNorm: pin.xNorm,
      pinYNorm: pin.yNorm,
    });
    const bearing = puttLineBearingDeg(ball.xNorm, ball.yNorm, pin.xNorm, pin.yNorm);

    console.log("[SS][greenVectors] path samples from vectors", {
      ball, pin, bearing, path,
    });

    setVectorPath(path);
    setVectorPathBearingDeg(bearing);

    // Also keep the manual/advanced fields populated as a sane fallback —
    // they're only actually used by the read when no sample above has
    // direction data (see usableVectorPath in the puttRead memo).
    if (path.length) {
      const start = path[0].slopeDeg;
      const mid = path[Math.floor((path.length - 1) / 2)].slopeDeg;
      const apex = Math.max(...path.map((p) => p.slopeDeg));
      if (start > 0) setThetaStartDeg(start);
      if (mid > 0) setThetaMidDeg(mid);
      if (apex > 0) setThetaApexDeg(apex);
    }
  }, [
    greenVectors,
    resolvedBallNorm,
    resolvedPinNorm,
    vectorFrameCalibration,
  ]);

  // -------------------------------------------------------------------------
  // Calculate shot: build ShotInputs and call engine
  // -------------------------------------------------------------------------
  // Build ShotInputs from your UI state fields.
  // Replace placeholders with your real form state variables.
  const DEFAULT_INPUTS: ShotInputs = {
    rawYardage: 137,
    windSpeedMph: 5,
    windDirectionDeg: 324,
    slopeYds: 2,
    temperatureF: 70,
    humidityPct: 40,
    energyPct: 100,
    lieType: "fairway",
    handicap: DEFAULT_PROFILE.handicap ?? 15,
  }

  const [inputs, setInputs] = useState<ShotInputs>(DEFAULT_INPUTS);
  const [yardageSource, setYardageSource] = useState<"manual" | "auto">("manual");
  const [lastYardageTouchedAt, setLastYardageTouchedAt] = useState<number>(Date.now());

  // Declared here (after both inputs and effectiveShotAzimuthDeg) to avoid
  // temporal dead zone. In manual mode derives absolute FROM bearing from the
  // shot-relative offset so "tailwind" stays correct mid-dogleg.
  const effectiveWindFromDeg =
    windDirectionMode === "manual"
      ? normalizeDeg360(effectiveShotAzimuthDeg + manualRelativeWindBodyDeg)
      : Number(inputs.windDirectionDeg) || 0;
  const getSignedSlopeYds = (
    direction: "level" | "uphill" | "downhill",
    magnitude: number,
  ) => {
    const mag = Number.isFinite(magnitude) ? Math.abs(magnitude) : 0;
    if (direction === "downhill") return -mag;
    if (direction === "uphill") return mag;
    return 0;
  };

  useEffect(() => {
    const slopeYds = getSignedSlopeYds(slopeDirection, slopeMagnitude);
    setInputs((prev) => ({
      ...prev,
      slopeYds: slopeYds,
    }));
  }, [slopeDirection, slopeMagnitude]);

  
  
  useEffect(() => {
    // A pin-sheet entry (loaded once pre-round via "Set today's pins") is the
    // real, tapped-in pin position — prefer it over the front/middle/back
    // approximation whenever the target is the pin itself.
    const sheetPin = targetType === "pin" ? pinSheet[hole] : undefined;
    if (sheetPin && sheetPin.lat != null && sheetPin.lng != null) {
      console.log("[SS][target resolve] using pin-sheet pin", { hole, sheetPin });
      setTargetLat(sheetPin.lat);
      setTargetLng(sheetPin.lng);
      return;
    }

    const resolved = resolveTargetCoordinates({
      courseId,
      hole,
      targetType,
      pinDepth: pinDepthThird,
      pinLateral: pinLateralThird,
      playerLat,
      playerLng,
    });

    console.log("[SS][target resolve]", {
      courseId,
      hole,
      holeType: typeof hole,
      targetType,
      pinDepthThird,
      pinLateralThird,
      resolved,
    });

    setTargetLat(resolved?.lat ?? null);
    setTargetLng(resolved?.lng ?? null);
  }, [courseId, hole, targetType, pinDepthThird, pinLateralThird, playerLat, playerLng, pinSheet]);
  
  useEffect(() => {
    if (
      playerLat == null ||
      playerLng == null ||
      targetLat == null ||
      targetLng == null
    ) {
      setShotAzimuthDeg(null);
      return;
    }

    const bearing = bearingDeg(playerLat, playerLng, targetLat, targetLng);
    setShotAzimuthDeg(Number.isFinite(bearing) ? bearing : null);
  }, [playerLat, playerLng, targetLat, targetLng]);
  
  useEffect(() => {
    if (playerLat == null || playerLng == null) {
      setDistanceToGreenCenterYds(null);
      setIsNearGreen(false);
      setIsOnGreenLikely(false);
      return;
    }

    const greenCenter = resolveTargetCoordinates({
      courseId,
      hole,
      targetType: "center",
      pinDepth: pinDepthThird,
      pinLateral: pinLateralThird,
    });

    if (!greenCenter) {
      setDistanceToGreenCenterYds(null);
      setIsNearGreen(false);
      setIsOnGreenLikely(false);
      return;
    }

    const distYds = haversineYards(
      playerLat,
      playerLng,
      greenCenter.lat,
      greenCenter.lng
    );

    // Reject implausible distances — bad preset coordinate data
    if (distYds > 600) {
      console.warn("[SS][greenCenter] implausible distance — bad preset coordinates", {
        hole, courseId, distYds, greenCenter,
      });
      setDistanceToGreenCenterYds(null);
      setIsNearGreen(false);
      setIsOnGreenLikely(false);
      return;
    }

    // Distance is measured from green CENTER, and real greens run 25-30+
    // yards across (see the green-book gridlines) — 15yd was rejecting
    // players standing at the front/back edge of a normal-sized green.
    const near = distYds <= 40;
    const onLikely = distYds <= 25;

    setDistanceToGreenCenterYds(distYds);
    setIsNearGreen(near);
    setIsOnGreenLikely(onLikely);

    if (near) {
      wasNearCurrentGreenRef.current = true;
    }

    if (!autoAdvanceHole || hole >= 18) {
      return;
    }

    // Signal 1 (preferred where available): arriving at the NEXT hole's
    // tee is a more direct, more immediate trigger than inferring it from
    // having walked far enough away from the current green — it's also
    // what actually flagged the gap in the first place (hole N's green
    // commonly stays closer than hole N+1's well after teeing off on N+1,
    // so the green-only signal below can lag). Only Brentwood has real
    // teeGround coordinates surveyed so far; other courses fall through
    // to signal 2.
    const nextTee = getHoleTargetPreset(courseId, hole + 1)?.teeGround;
    if (nextTee) {
      const distToNextTeeYds = haversineYards(
        playerLat, playerLng, nextTee.lat, nextTee.lng
      );
      const NEAR_NEXT_TEE_YDS = 40;
      if (distToNextTeeYds <= NEAR_NEXT_TEE_YDS && !near) {
        const nextHole = hole + 1;
        console.log("[SS][auto-advance] arrived at next tee", { hole, nextHole, distToNextTeeYds });
        // Clear synchronously (not just via the separate [hole] effect below):
        // this effect can re-run for the new hole before that effect flushes,
        // and a stale `true` here — paired with the new hole's own (still
        // large, we just arrived at its tee) distance-to-green — used to
        // satisfy signal 2 immediately and skip straight to hole+2.
        wasNearCurrentGreenRef.current = false;
        setAutoAdvanceNotice(`Advanced to hole ${nextHole}`);
        handleHoleChange(nextHole);
        return;
      }
    }

    // Signal 2 (fallback): auto-advance once we've clearly left the green
    // we were just on. The next hole is always current+1 — course routing
    // is sequential, not something to infer by searching for the nearest
    // green (that would false-trigger on courses where holes loop back
    // near each other). The 100yd leave-margin avoids flipping on GPS
    // jitter near the green edge, and comparing against THIS hole's own
    // green (not hole+1's) avoids the failure mode where hole N's green
    // stays closer than hole N+1's well after teeing off on N+1.
    const LEAVE_GREEN_MARGIN_YDS = 100;
    if (wasNearCurrentGreenRef.current && distYds > LEAVE_GREEN_MARGIN_YDS) {
      const nextHole = hole + 1;
      console.log("[SS][auto-advance] leaving green", { hole, nextHole, distYds });
      wasNearCurrentGreenRef.current = false;
      setAutoAdvanceNotice(`Advanced to hole ${nextHole}`);
      handleHoleChange(nextHole);
    }
  }, [playerLat, playerLng, courseId, hole, pinDepthThird, pinLateralThird, autoAdvanceHole]);

  // Reset "was on the current green" tracking whenever the hole changes
  // (manually or via auto-advance) so detection starts fresh each hole.
  useEffect(() => {
    wasNearCurrentGreenRef.current = false;
    setPuttNumber(1);
    setPuttHoled(null);
    setPuttReadQuality(null);
    setPuttOutcomeStatus("idle");
    setPuttOutcomeError(null);
  }, [hole]);

  useEffect(() => {
    if (!autoAdvanceNotice) return;
    const t = window.setTimeout(() => setAutoAdvanceNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [autoAdvanceNotice]);

  // --- simple input handlers for the test rig UI ---

  type NumericField =
    | "rawYardage"
    | "windSpeedMph"
    | "windDirectionDeg"
    | "slopeYds"
    | "temperatureF"
    | "humidityPct"
    | "energyPct"
    | "handicap";

  const handleNumericChange =
    (field: keyof ShotInputs) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      handleShotInputChange(field, raw === "" ? 0 : Number(raw));
    };

  const handleLieTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as ShotInputs["lieType"];
    setInputs((prev) => ({
      ...prev,
      lieType: value,
    }));
  };

  // NEW: generic helper and wind-direction options
  const handleShotInputChange = <K extends keyof ShotInputs>(
    key: K,
    value: ShotInputs[K],
  ) => {
    setInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Values are body-frame offsets from shot direction (0=headwind, 180=tailwind).
  // Stored 0-315 to avoid negatives; conversion: absFrom = shotAzimuth + value.
  const WIND_DIRECTION_OPTIONS = [
    { label: "Into face — headwind", value: 0 },
    { label: "Right-front quarter — hurting right", value: 45 },
    { label: "Right cross — right shoulder", value: 90 },
    { label: "Right-rear quarter — helping right", value: 135 },
    { label: "From behind — tailwind", value: 180 },
    { label: "Left-rear quarter — helping left", value: 225 },
    { label: "Left cross — left shoulder", value: 270 },
    { label: "Left-front quarter — hurting left", value: 315 },
  ];

  const handlePlayerIdInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPlayerIdInput(e.target.value);
  };

  const handleSaveProfileAndBag = async () => {
    // 1) Guard: must have a playerId
    if (!playerId || !playerId.trim()) {
      console.warn("[SS] Cannot save: playerId is empty");
      return;
    }

    // 2) Build the object you want to save
    //    (This is the key: Firestore saves what you pass in here.)
    const profileToSave: PlayerProfile = {
      ...playerProfile,

      // If your bag lives somewhere else (like `bag` or `clubs` state),
      // you MUST include it here. If it's already inside `playerProfile`,
      // then you do NOT need to add anything.
      //
      // Example only (use your real field name if needed):
      // bag: bag,
    };
    console.log("[SS] currentBag before save", currentBag);
    // 3) Call DAL save function
    const ok = await savePlayerProfile(playerId, profileToSave);

    if (ok) {
      console.log("[SS] Saved player profile:", playerId);
    } else {
      console.warn("[SS] Save player profile failed/offline");
    }
  };

  // Edit handlers for bag carry distances
  const handleBagCarryChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);

      setCurrentBag((prev) =>
        prev.map((club, i) =>
          i === index
            ? {
                ...club,
                carryYards: Number.isFinite(value) ? value : club.carryYards,
              }
            : club,
        ),
      );
    };

  const handleRefreshLocation = async (): Promise<{
    lat: number;
    lng: number;
  }> => {
    if (!navigator.geolocation) {
      setGpsError("Geolocation is not supported on this device/browser.");
      setGpsStatus("error");
      throw new Error("Geolocation is not supported on this device/browser.");
    }

    // Return the watchPosition fix immediately whenever it's recent, even if
    // accuracy is worse than GPS_ACCURACY_THRESHOLD_M (e.g. under tree cover
    // near a dogleg). Blocking here on accuracy alone caused multi-second
    // stalls at exactly the spots where GPS is hardest to improve quickly.
    // Degraded accuracy is surfaced via the GPS badge instead — compare it
    // against the cart's number rather than waiting on a fresh fix.
    const fixAge = lastGpsFixAt == null ? Infinity : Date.now() - lastGpsFixAt;
    if (fixAge < 10_000 && playerLat != null && playerLng != null) {
      console.log("[SS][gps] reusing fresh watch fix", {
        fixAge,
        playerLat,
        playerLng,
        accuracyM: lastGpsAccuracyM,
        degraded: lastGpsAccuracyM != null && lastGpsAccuracyM > GPS_ACCURACY_THRESHOLD_M,
      });
      return { lat: playerLat, lng: playerLng };
    }

    // No usable fix at all (cold start or watch stalled) — fall back to a
    // one-shot request. This is the only case still allowed to block.
    setGpsStatus("locating");
    setGpsError(null);

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setPlayerLat(lat);
          setPlayerLng(lng);
          setGpsStatus("player-fixed");
          setLastGpsFixAt(Date.now());
          setLastGpsAccuracyM(
            typeof position.coords.accuracy === "number"
              ? position.coords.accuracy
              : null
          );

          console.log("[SS][gps fix]", {
            lat, lng, accuracyM: position.coords.accuracy, at: new Date().toISOString(),
          });

          resolve({ lat, lng });
        },
        (error) => {
          const message = error.message || "Failed to get player location.";
          setGpsError(message);
          setGpsStatus("error");
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 6000,
          maximumAge: 10_000,
        }
      );
    });
  };
  const handleLoadWeather = async () => {
    console.log("[SS][weather] online", navigator.onLine);
    console.log("[SS][weather] player", playerLat, playerLng);

    if (playerLat == null || playerLng == null) {
      setWeatherError("Player location is required to load weather.");
      setWeatherStatus("error");
      return;
    }

    try {
      setWeatherStatus("loading");
      setWeatherError(null);

      const wx = await getAccuCurrentConditionsByLatLng(playerLat, playerLng);
      console.log("[SS][weather] response", wx);

      if (wx.windSpeedMph != null) {
        handleShotInputChange("windSpeedMph", wx.windSpeedMph);
      }
      if (wx.windGustMph != null) {
        setWindGustMph(wx.windGustMph);
      }
      if (wx.windDirectionDeg != null) {
        handleShotInputChange("windDirectionDeg", wx.windDirectionDeg);
      }
      if (wx.temperatureF != null) {
        handleShotInputChange("temperatureF", wx.temperatureF);
      }
      if (wx.humidityPct != null) {
        handleShotInputChange("humidityPct", wx.humidityPct);
      }

      setWeatherStatus("loaded");
    } catch (err: any) {
      console.error("[SS][weather] load failed", err);
      setWeatherError(String(err?.message ?? err ?? "Failed to load weather."));
      setWeatherStatus("error");
    }
  };
  const handleLoadElevation = async () => {
    console.log("[SS][elevation] online", navigator.onLine);
    console.log("[SS][elevation] player", playerLat, playerLng);
    console.log("[SS][elevation] target", targetLat, targetLng);
    console.log("[SS][elevation] targetType", targetType);
    console.log("[SS][elevation] hole", hole, courseId);
    if (
      playerLat == null ||
      playerLng == null ||
      targetLat == null ||
      targetLng == null
    ) {
      setElevationError(
        "Player and target locations are required for elevation.",
      );
      setElevationStatus("error");
      return;
    }

    try {
      setElevationStatus("loading");
      setElevationError(null);

      const elev = await getElevationDeltaFt({
        fromLat: playerLat,
        fromLng: playerLng,
        toLat: targetLat,
        toLng: targetLng,
      });

      setPlayerProfile((prev) => ({
        ...prev,
        altitudeFt: Math.round(elev.from.elevationFeet),
      }));

      const signedSlopeYds = Math.max(
        -30,
        Math.min(30, elev.deltaFeet / 3)
      );

      setInputs((prev) => ({
        ...prev,
        slopeYds: Number.isFinite(signedSlopeYds)
          ? Number(signedSlopeYds.toFixed(1))
          : prev.slopeYds,
      }));

      if (signedSlopeYds > 0) {
        setSlopeDirection("uphill");
        setSlopeMagnitude(Math.abs(Number(signedSlopeYds.toFixed(1))));
      } else if (signedSlopeYds < 0) {
        setSlopeDirection("downhill");
        setSlopeMagnitude(Math.abs(Number(signedSlopeYds.toFixed(1))));
      } else {
        setSlopeDirection("level");
        setSlopeMagnitude(0);
      }

      setElevationStatus("loaded");
    } catch (err: any) {
      console.error("[SS][elevation] load failed", err);
      setElevationError(
        String(err?.message ?? err ?? "Failed to load elevation."),
      );
      setElevationStatus("error");
    }
  };
  const handleLoadTodayShots = async () => {
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, "shotLogs"),
        where("createdAt", ">=", startOfToday),
        orderBy("createdAt", "desc"),
      );

      const snapshot = await getDocs(q);

      const shots = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("[SS][today shots]", shots);

      console.table(
        shots.map((s: any) => ({
          createdAt: s.createdAt,
          playerId: s.playerId,
          courseId: s.courseId,
          hole: s.hole,
          rawYardage: s.rawYardage,
          playsLikeYards: s.playsLikeYards,
          windSpeedMph: s.windSpeedMph,
          windFromDeg: s.windFromDeg,
          windToDeg: s.windToDeg,
          windBucket: s.windBucket,
          club: s.club,
          confidence: s.confidence,
        })),
      );
    } catch (err: any) {
      console.error("[SS][today shots] load failed", err);
    }
  };
  const handleHoleChange = (nextHole: number) => {
    console.log("[SS][hole change] hard reset", {
      fromHole: hole,
      toHole: nextHole,
    });

    setHole(nextHole);

    // Target context must be recalculated for the new hole
    setTargetType("pin");
    setTargetLat(null);
    setTargetLng(null);
    setShotAzimuthDeg(null);
    setDistanceToGreenCenterYds(null);
    setIsNearGreen(false);
    setIsOnGreenLikely(false);

    // Yardage must not persist hole-to-hole; reset to auto so GPS refills it
    setYardageSource("auto");
    setLastYardageTouchedAt(Date.now());
    setLastHoleChangedAt(Date.now());

    setInputs((prev) => ({
      ...prev,
      rawYardage: 0,
      slopeYds: 0,
    }));

    // Slope must not persist hole-to-hole
    setSlopeDirection("level");
    setSlopeMagnitude(0);

    // Recommendation/result state must clear
    setLastRec(null);
    setTargetRec(null);
    setLastShotOutcomeKey(null);
    setShotExecution("");
    setRecommendationQuality("");
    setOutcomeStatus("idle");
    setOutcomeError(null);

    // GPS state: keep player GPS available, but force target recomputation
    console.log("[SS][hole change] cleared target, yardage, slope, rec");
  };
  const gpsFixAgeMs =
    lastGpsFixAt == null ? null : Date.now() - lastGpsFixAt;

  const gpsFixFresh =
    lastGpsFixAt != null &&
    lastGpsFixAt > lastHoleChangedAt &&      // fix must postdate the hole change
    gpsFixAgeMs != null &&
    gpsFixAgeMs <= 30_000 &&
    (lastGpsAccuracyM == null || lastGpsAccuracyM <= GPS_ACCURACY_THRESHOLD_M);
  const handleCalculateShot = async () => {    try {
      console.log("[SS] handleCalculateShot fired");
      if (!Number.isFinite(inputs.rawYardage) || inputs.rawYardage <= 0) {
        alert("Enter raw yardage before calculating.");
        console.warn("[SS][yardage] blocked calculation — missing raw yardage", {
          hole,
          targetType,
          rawYardage: inputs.rawYardage,
        });
        return;
      }
    if (yardageLooksStale && gpsYardageUsable) {
        const useGps = window.confirm(
          `Manual yardage is ${inputs.rawYardage} yds, but GPS target is approximately ${autoTargetYardage?.toFixed(
            0
          )} yds.

      Press OK to USE GPS yardage.
      Press Cancel to KEEP manual yardage.`
        );

        if (useGps && autoTargetYardage != null) {
          const gpsYds = Math.round(autoTargetYardage);
          handleShotInputChange("rawYardage", gpsYds);
          setYardageSource("auto");
        } else {
          console.warn("[SS][yardage] keeping manual yardage", {
            manual: inputs.rawYardage,
            gps: autoTargetYardage,
          });
        }
    }
      console.log("[SS] inputs", inputs);

      // --- ENSURE FRESH GPS + ELEVATION (AUTO MODE) ---
      // Use the watchPosition fix if recent; only block on a new fetch when stale.
      const calcFixAge = lastGpsFixAt == null ? Infinity : Date.now() - lastGpsFixAt;
      const hasFreshFix =
        calcFixAge < 10_000 &&
        playerLat != null &&
        playerLng != null &&
        (lastGpsAccuracyM == null || lastGpsAccuracyM <= GPS_ACCURACY_THRESHOLD_M);

      let calcPlayerLat: number;
      let calcPlayerLng: number;

      if (hasFreshFix) {
        console.log("[SS][auto] using fresh watch fix", { calcFixAge, playerLat, playerLng });
        calcPlayerLat = playerLat!;
        calcPlayerLng = playerLng!;
      } else if (playerLat != null && playerLng != null) {
        // Stale but present — use it; elevation will still run
        console.log("[SS][auto] using stale GPS fix (no refresh available)");
        calcPlayerLat = playerLat;
        calcPlayerLng = playerLng;
      } else {
        // No GPS at all — attempt refresh but don't block on failure
        console.log("[SS][auto] no GPS fix, attempting refresh");
        try {
          const freshPlayer = await handleRefreshLocation();
          if (freshPlayer?.lat != null && freshPlayer?.lng != null) {
            calcPlayerLat = freshPlayer.lat;
            calcPlayerLng = freshPlayer.lng;
          } else {
            // Proceed without GPS — elevation/bearing will be skipped
            calcPlayerLat = 0;
            calcPlayerLng = 0;
            console.warn("[SS][auto] proceeding without GPS — manual yardage only");
          }
        } catch {
          calcPlayerLat = 0;
          calcPlayerLng = 0;
          console.warn("[SS][auto] GPS unavailable — proceeding with manual yardage");
        }
      }
    const calcTargetLat = targetLat;
    const calcTargetLng = targetLng;
    const isLayupShot = targetType === "layup";

    if (
      !isLayupShot &&
      (
        calcPlayerLat == null ||
        calcPlayerLng == null ||
        calcTargetLat == null ||
        calcTargetLng == null
      )
    ) {
      console.warn("[SS][auto] missing coordinates after refresh", {
        targetType,
        calcPlayerLat,
        calcPlayerLng,
        calcTargetLat,
        calcTargetLng,
      });

      alert("Target not resolved. Select a pin or switch to layup.");

      return;
    }

    console.log("[SS][elevation inputs]", {
      playerLat: calcPlayerLat,
      playerLng: calcPlayerLng,
      targetLat: calcTargetLat,
      targetLng: calcTargetLng,
    });
    const distanceToTargetYds =
      !isLayupShot &&
      calcTargetLat != null &&
      calcTargetLng != null
        ? haversineYards(
            calcPlayerLat!,
            calcPlayerLng!,
            calcTargetLat!,
            calcTargetLng!
          )
        : Number.POSITIVE_INFINITY;
    
      // Fetch elevation whenever real GPS + target coordinates exist.
      const hasRealGps = calcPlayerLat !== 0 || calcPlayerLng !== 0;
      if (hasRealGps && calcTargetLat != null && calcTargetLng != null) {
      const elevArgs = {
        fromLat: calcPlayerLat!,
        fromLng: calcPlayerLng!,
        toLat: calcTargetLat,
        toLng: calcTargetLng,
      };
      let elev = null;
      try {
        elev = await getElevationDeltaFt(elevArgs);
      } catch {
        // Retry once after 2 s — avoids hitting OpenTopoData's 1 req/sec limit twice.
        try {
          await new Promise(r => setTimeout(r, 2000));
          elev = await getElevationDeltaFt(elevArgs);
        } catch (elevErr: any) {
          console.warn("[SS][auto] elevation failed after retry", elevErr);
          setElevationSource("fallback");
        }
      }

        if (!elev || !Number.isFinite(elev.deltaFeet)) {
          console.warn("[SS][auto] invalid elevation payload", elev);
          setSlopeDirection("level");
          setSlopeMagnitude(0);
          setElevationSource("fallback");
        } else {
          setElevationDeltaFt(elev.deltaFeet);
          const rawSlopeYdsAuto = elev.deltaFeet / 3;
          const slopeYdsAuto = rawSlopeYdsAuto;
          const absSlopeYdsAuto = Math.abs(slopeYdsAuto);

          const slopePercent =
            distanceToTargetYds > 0
              ? (elev.deltaFeet / (distanceToTargetYds * 3)) * 100
              : 0;
          const slopeContext = {
            approach: {
              source: "gps-elevation" as const,
              deltaFeet: elev.deltaFeet,
              distanceYds: distanceToTargetYds,
              slopeYds: rawSlopeYdsAuto,
            },
            green: {
              source: "none" as const,
            },
          };

          console.log("[SS][slopeContext][approach]", slopeContext.approach);

          const slopeDeg =
            Math.atan(slopePercent / 100) * (180 / Math.PI);
          console.log("[SS][auto] elevation", {
            deltaFeet: elev.deltaFeet,
            distanceToTargetYds,
            rawSlopeYdsAuto,
            slopeYdsAuto,
            absSlopeYdsAuto,
            slopePercent,
            slopeDeg,
          });

            if (absSlopeYdsAuto > 15) {
              console.warn("[SS][auto] rejecting implausible slope", {
                deltaFeet: elev.deltaFeet,
                rawSlopeYdsAuto,
                slopeYdsAuto,
                distanceToTargetYds,
              });

              setSlopeDirection("level");
              setSlopeMagnitude(0);
              setElevationSource("fallback");
            } else if (slopeYdsAuto > 0) {
              setSlopeDirection("uphill");
              setSlopeMagnitude(Number(Math.abs(slopeYdsAuto).toFixed(1)));
              setElevationSource("gps");
            } else if (slopeYdsAuto < 0) {
              setSlopeDirection("downhill");
              setSlopeMagnitude(Number(Math.abs(slopeYdsAuto).toFixed(1)));
              setElevationSource("gps");
            } else {
              setSlopeDirection("level");
              setSlopeMagnitude(0);
              setElevationSource("gps");
            }
        }
      } else {
        console.log("[SS][auto] skipping elevation — no target coordinates", { targetType });
        setSlopeDirection("level");
        setSlopeMagnitude(0);
      }
    const effectiveHoleAzimuthDeg = holeAzimuthDeg;
    const effectiveShotAzimuthDeg =
      typeof shotAzimuthDeg === "number"
        ? shotAzimuthDeg
        : effectiveHoleAzimuthDeg;
    
      const rawWindFromDeg = effectiveWindFromDeg;
      const rawWindToDeg = (rawWindFromDeg + 180) % 360;
    // --- compute shot-relative wind ---

    // Vector-frame (for compass arrows)
    const relativeWindDegDisplay = relativeWindDeg(
      rawWindToDeg,
      effectiveShotAzimuthDeg
    );

    // Body-frame (for golfer feel / labels)
    const relativeWindFromDegDisplay = relativeWindFromDeg(
      rawWindFromDeg,
      effectiveShotAzimuthDeg
    );

    // Use BODY FRAME for bucket (this matches your shoulder model)
    const windBucketDisplay = classifyWindBodyFrame(
      relativeWindFromDegDisplay
    );

    // Debug — critical for on-course validation
    console.log("[SS][wind debug]", {
      hole,
      targetType,
      holeAzimuthDeg,
      shotAzimuthDeg,
      effectiveShotAzimuthDeg,
      windFromDeg: rawWindFromDeg,
      windToDeg: rawWindToDeg,
      vectorRelativeDeg: relativeWindDegDisplay,
      bodyRelativeDeg: relativeWindFromDegDisplay,
      bucket: windBucketDisplay,
    });
    

      console.log("[SS][geometry]", {
        hole,
        courseId,
        playerLat,
        playerLng,
        targetLat,
        targetLng,
        holeAzimuthDeg,
        shotAzimuthDeg,
        effectiveShotAzimuthDeg,
      });

      const shotRelativeWindDeg = relativeWindDeg(
        rawWindToDeg,
        effectiveShotAzimuthDeg,
      );
  
      const windBucket = classifyRelativeWind(shotRelativeWindDeg);
    console.log("[SS][wind debug]", {
      rawWindFromDeg,
      rawWindToDeg,
      effectiveShotAzimuthDeg,
      shotRelativeWindDeg,
      windBucket,
    });

      const windDirBin = Number.isFinite(shotRelativeWindDeg)
        ? Math.min(7, Math.floor(shotRelativeWindDeg / 45))
        : null;

      const shotInputs: ShotInputs = {
        rawYardage: Number(inputs.rawYardage) || 0,
        windSpeedMph: Number(inputs.windSpeedMph) || 0,
        windDirectionDeg: shotRelativeWindDeg,
        slopeYds: Number(inputs.slopeYds) || 0,
        temperatureF: Number(inputs.temperatureF) || 70,
        humidityPct: Number(inputs.humidityPct) || 50,
        energyPct: Number(inputs.energyPct) || 100,
        lieType: inputs.lieType,
        handicap: Number(inputs.handicap) || 15,
      };

      const effectiveProfile: PlayerProfile = {
        ...playerProfile,
        id: canonId(playerId ?? "") || canonId(playerProfile.id ?? ""),
        bag: currentBag,
      };

      console.log("[SS] shotInputs", shotInputs);
      console.log("[SS] effectiveProfile", effectiveProfile);
    const finalSlopeYds = inputs.slopeYds ?? 0;
    console.log("[SS][calc snapshot]", {
      hole,
      targetType,
      yardageSource,
      rawYardage: shotInputs.rawYardage,
      gpsTargetYardage: autoTargetYardage,
      finalSlopeYds,
      windSpeedMph: shotInputs.windSpeedMph,
      windDirectionDeg: shotInputs.windDirectionDeg,
      energyPct: shotInputs.energyPct,
      lieType: shotInputs.lieType,
    });
    const rec = computeShotRecommendation(
      {
        ...shotInputs,
        slopeYds: finalSlopeYds,
      },
      effectiveProfile
    );

    console.log("[SS][rec raw]", rec);
    console.log("[SS][effectiveProfile]", effectiveProfile);
    console.log("[SS][bag]", effectiveProfile?.bag);

    if (!rec) {
      console.warn("[SS][rec] FAILED — no recommendation returned", {
        shotInputs,
        effectiveProfile,
        bag: effectiveProfile?.bag,
      });
      return;
    }

    setLastRec(rec);
      console.log("[SS][rec accepted]", rec);

      if (isLayupShot) {
      setTargetRec(null);
      console.log("[SS][TARGET] skipped for layup");
    } else {
      const target = computeTargetRecommendation({
        shotDistanceYds: rec.playsLikeYards,
        pinLateralThird,
        pinDepthThird,
        leftPenalty,
        rightPenalty,
        frontPenalty,
        backPenalty,
        leftBunker: "none",
        rightBunker: "none",
        frontBunker: "none",
        backBunker: "none",
      });

      setTargetRec(target);
      console.log("[SS][TARGET]", target);
    }
      const pid = canonId(playerId ?? "") || canonId(playerProfile.id ?? "");
      if (!pid) throw new Error("No playerId available for logging");

      const nextSeq = shotSeq + 1;
      setShotSeq(nextSeq);

      try {
        localStorage.setItem(LS_SHOT_SEQ, String(nextSeq));
      } catch {}

      console.log("[SS][shot log] about to write", {
        playerId: pid,
        courseId,
        hole,
        sessionId,
        shotSeq: nextSeq,
      });

      const ok = await logShot({
        playerId: pid,
        courseId,
        hole,
        sessionId,
        shotSeq: nextSeq,
        deviceId,

        windFromDeg: rawWindFromDeg,
        windToDeg: rawWindToDeg,
        holeAzimuthDeg: effectiveHoleAzimuthDeg,
        shotAzimuthDeg: effectiveShotAzimuthDeg,
        windDirBin,
        windBucket,

        appVersion: "1.0.0-beta",
        algoVersion: "engine-v1",
        mode: "beta",

        ...shotInputs,

        altitudeFt: effectiveProfile.altitudeFt ?? null,

        club: rec.club,
        clubCarryYards: rec.clubCarryYards,

        playsLikeYards: rec.playsLikeYards,
        playsLikeDeltaYds: rec.playsLikeYards - shotInputs.rawYardage,

        expectedProximityFt: rec.expectedProximityFt,

        confidence: rec.confidence,
        confidenceScore: rec.confidenceScore,
        confidenceReasons: rec.confidenceReasons,

        explanation: rec.explanation,
      });

      console.log("[SS][shot log] result", ok);

      if (ok) {
        const outcomeKey = `${pid}_${sessionId}_${nextSeq}`;
        setLastShotOutcomeKey(outcomeKey);
        setShotExecution(null);
        setRecommendationQuality(null);
        setOutcomeStatus("idle");
        setOutcomeError(null);
      }

      if (!ok) console.warn("[SS] Shot log skipped/offline");
    } catch (err) {
      console.error("[SS] handleCalculateShot failed", err);
    }
  };

  const handleSaveShotOutcome = async () => {
    if (!lastShotOutcomeKey) {
      setOutcomeError(
        "No logged shot is available yet. Calculate a shot first.",
      );
      setOutcomeStatus("error");
      return;
    }

    if (!shotExecution || !recommendationQuality) {
      setOutcomeError(
        "Please select both Shot execution and Recommendation quality.",
      );
      setOutcomeStatus("error");
      return;
    }

    try {
      setOutcomeStatus("saving");
      setOutcomeError(null);

      await setDoc(
        doc(db, "shotOutcomes", lastShotOutcomeKey),
        {
          shotKey: lastShotOutcomeKey,
          shotExecution,
          recommendationQuality,

          // --- NEW: green-relative outcome context ---
          greenResultContext: {
            puttDistanceFt: Number(puttDistanceFt ?? 0),

            fallLineXFt: Number(fallLineXFt ?? 0),
            fallLineYFt: Number(fallLineYFt ?? 0),

            sideOfFallLine: fallLineXFt > 0 ? "right" : fallLineXFt < 0 ? "left" : "on",
            heightVsHole: fallLineYFt > 0 ? "above" : fallLineYFt < 0 ? "below" : "level",

            thetaStartDeg: Number(thetaStartDeg ?? 0),
            thetaMidDeg: Number(thetaMidDeg ?? 0),
            thetaApexDeg: Number(thetaApexDeg ?? 0),

            angleToFallLineDeg: Number(angleToFallLineDeg ?? 0),
            angleToMTBDeg: Number(angleToMTBDeg ?? 0),

            terrainBiasDeg: 0,
            stimp: Number(greenStimp ?? 10),

            breakDirection: puttRead.breakDirection ?? "unknown",
            aimInches: 0,
          },

          // --- NEW: learning scaffold (NO learning yet) ---
          learningContext: {
            predictedProximityFt: lastRec?.expectedProximityFt ?? null,
            actualPuttDistanceFt: Number(puttDistanceFt ?? 0),

            proximityErrorFt:
              lastRec?.expectedProximityFt != null
                ? Number(puttDistanceFt ?? 0) - lastRec.expectedProximityFt
                : null,

            club: lastRec?.club ?? null,
            playsLikeYards: lastRec?.playsLikeYards ?? null,
            targetType,

            windSpeedMph: inputs.windSpeedMph ?? null,
            windDirectionDeg: inputs.windDirectionDeg ?? null,
            slopeYds: inputs.slopeYds ?? 0,
            energyPct: inputs.energyPct ?? null,

            hole,

            shotTimestamp: Date.now(),
            courseName: courseId ?? null,
            holeNumber: hole ?? null,
          },

          createdAt: serverTimestamp(),
          savedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setOutcomeStatus("saved");
    } catch (err: any) {
      console.error("[SS][outcome] save failed", err);
      setOutcomeError(err?.message ?? "Failed to save shot outcome.");
      setOutcomeStatus("error");
    }
  };
  const rawWindDegDisplay = effectiveWindFromDeg;
  const rawWindToDegDisplay = (rawWindDegDisplay + 180) % 360;
  const effectiveShotAzimuthDisplay = shotAzimuthDeg ?? holeAzimuthDeg;

  const relativeWindDegDisplay = relativeWindDeg(
    rawWindToDegDisplay,
    effectiveShotAzimuthDisplay,
  );
  const relativeWindFromDegDisplay = relativeWindFromDeg(
    rawWindDegDisplay,
    effectiveShotAzimuthDisplay,
  );
  const windBodyBucketDisplay = classifyWindBodyFrame(relativeWindFromDegDisplay);
  console.log("[SS][wind debug]", {
    windFromDeg: rawWindDegDisplay,
    windToDeg: rawWindToDegDisplay,
    shotAzimuthDeg: effectiveShotAzimuthDisplay,
    relativeDeg: relativeWindDegDisplay,
  });
  const windBucketDisplay = classifyRelativeWind(relativeWindDegDisplay);

  const handleNextHole = () => {
    const nextHole = Math.min(hole + 1, 18);
    handleHoleChange(nextHole);

    setPinLateralThird("center");
    setPinDepthThird("middle");

    setLeftPenalty("none");
    setRightPenalty("none");
    setFrontPenalty("none");
    setBackPenalty("none");

    setActiveTab("shot");
  };
  // and useEffect to load the green image when `hole` changes.
  const handleNextShot = async () => {
    setLastRec(null);
    setTargetRec(null);
    setShotExecution("solid");
    setRecommendationQuality("agree");
    setOutcomeStatus("idle");
    setOutcomeError(null);
    setSlopeDirection("level");
    setSlopeMagnitude(0);
    // Keep same hole, weather, wind, player profile.
    // Refresh GPS so next-shot yardage can be recalculated.
    try {
      await handleRefreshLocation();
    } catch (err) {
      console.warn("[SS][next shot] GPS refresh failed", err);
    }
    console.log("[SS] Next Shot");
  };

  // Move on to the next putt on the same green — a one-putt is the
  // exception, not the assumption. Clears the putt-specific reads back to
  // their defaults so the next (shorter) putt gets a fresh read instead of
  // silently reusing the previous putt's numbers; green-wide settings
  // (stimp, rollout, terrain bias) carry over since those don't change
  // putt to putt.
  const handleNextPutt = () => {
    setPuttNumber((n) => n + 1);
    // The ball moved after putt #1 — carrying over the old tap would silently
    // feed the new putt's read from the previous putt's spot (wrong distance
    // AND wrong break, since both are derived from the ball→pin line).
    setTappedBallXNorm(null);
    setTappedBallYNorm(null);
    // Also block the GPS fallback from silently reusing a fix taken before
    // this putt started — GPS is coarse enough already (see the distance
    // fix) without additionally being stale. resolvedBallNorm requires a
    // fix newer than this timestamp before it'll use one.
    setLastPuttAdvancedAt(Date.now());
    setPuttDistanceFt(10);
    setThetaStartDeg(1.0);
    setThetaMidDeg(1.5);
    setThetaApexDeg(2.0);
    setSideOfFallLine("right");
    setHeightVsHole("above");
    setFallLineXFt(-10);
    setFallLineYFt(-1);
    setPuttHoled(null);
    setPuttReadQuality(null);
    setPuttOutcomeStatus("idle");
    setPuttOutcomeError(null);
  };

  // Logs the read this putt actually got (path-based or manual) against
  // what happened, the same way handleSaveShotOutcome does for the shot
  // recommendation — this is the data a future pass would tune the putt
  // model's weighting/K-constant against.
  const handleSavePuttOutcome = async () => {
    if (!puttHoled || !puttReadQuality) {
      setPuttOutcomeError("Please select both whether it was holed and read accuracy.");
      setPuttOutcomeStatus("error");
      return;
    }

    const pid = canonId(playerId ?? "") || canonId(playerProfile.id ?? "");
    if (!pid) {
      setPuttOutcomeError("No player ID available for logging.");
      setPuttOutcomeStatus("error");
      return;
    }

    const puttKey = `${pid}_${sessionId}_h${hole}_putt${puttNumber}_${Date.now()}`;

    try {
      setPuttOutcomeStatus("saving");
      setPuttOutcomeError(null);

      await setDoc(doc(db, "puttOutcomes", puttKey), {
        puttKey,
        playerId: pid,
        courseId,
        hole,
        puttNumber,
        sessionId,
        deviceId,

        holed: puttHoled,
        readQuality: puttReadQuality,

        // The full predicted read, exactly as shown to the player.
        predictedRead: {
          readMethod: puttRead.readMethod ?? "manual",
          pathSamplesUsed: puttRead.pathSamplesUsed ?? null,
          breakInches: puttRead.breakInches,
          breakDirection: puttRead.breakDirection,
          aimSideOfCup: puttRead.aimSideOfCup,
          aimOffsetInches: puttRead.aimOffsetInches,
          speedPastCupInches: puttRead.speedPastCupInches,
          aboveOrBelowHole: puttRead.aboveOrBelowHole,
          gradeSeverity: puttRead.gradeSeverity,
          multiBreakNote: puttRead.multiBreakNote,
          effectiveSlopeDeg: puttRead.effectiveSlopeDeg,
          thetaCross: puttRead.thetaCross,
          thetaGrade: puttRead.thetaGrade,
        },

        // Raw inputs the read was built from, so a future tuning pass can
        // recompute "what if" with different weights/K without re-deriving
        // any of this from the green book.
        inputs: {
          puttDistanceFt: Number(puttDistanceFt ?? 0),
          stimp: Number(greenStimp ?? 10),
          rolloutFt: Number(rolloutFt ?? 0),
          vectorPath: vectorPath.length ? vectorPath : null,
          vectorPathBearingDeg: vectorPath.length ? vectorPathBearingDeg : null,
          thetaStartDeg: Number(thetaStartDeg ?? 0),
          thetaMidDeg: Number(thetaMidDeg ?? 0),
          thetaApexDeg: Number(thetaApexDeg ?? 0),
          sideOfFallLine,
          heightVsHole,
        },

        createdAt: serverTimestamp(),
      });

      setPuttOutcomeStatus("saved");
    } catch (err: any) {
      console.error("[SS][putt outcome] save failed", err);
      setPuttOutcomeError(err?.message ?? "Failed to save putt outcome.");
      setPuttOutcomeStatus("error");
    }
  };

  const autoTargetYardage =
    playerLat != null &&
    playerLng != null &&
    targetLat != null &&
    targetLng != null
      ? haversineYards(playerLat, playerLng, targetLat, targetLng)
      : null;

  const gpsYardageUsable =
    gpsFixFresh &&
    autoTargetYardage != null &&
    Number.isFinite(autoTargetYardage) &&
    autoTargetYardage > 0 &&
    autoTargetYardage <= 400;

  const yardageDeltaFromGps =
    gpsYardageUsable
      ? Math.abs(Number(inputs.rawYardage || 0) - autoTargetYardage)
      : null;

  const yardageLooksStale =
    yardageSource === "manual" &&
    gpsYardageUsable &&
    yardageDeltaFromGps != null &&
    yardageDeltaFromGps >= 20;

  // Auto-fill/update yardage from GPS on every location refresh unless the
  // player has manually typed a value (yardageSource === "manual").
  useEffect(() => {
    if (gpsYardageUsable && autoTargetYardage != null && yardageSource !== "manual") {
      handleShotInputChange("rawYardage", Math.round(autoTargetYardage));
      setYardageSource("auto");
    }
  }, [gpsYardageUsable, autoTargetYardage]);

  // When on the green, auto-populate putt distance from the ball/pin
  // positions on the green diagram (puttBallToPinDistanceFt) rather than
  // raw phone GPS -- confirmed on course that GPS's ~10-20ft+ consumer
  // accuracy is too coarse for a putt-length distance (showed 33ft for an
  // actual 15ft putt). Falls back to GPS only when a diagram-based
  // distance isn't available at all (e.g. no calibration/vectors for this
  // hole yet).
  //
  // The diagram distance does NOT require isOnGreenLikely: once both the ball
  // and pin are placed on the green image (ball tapped by hand, pin from the
  // pin sheet), that distance is already the best available -- gating it on
  // GPS left the field stuck at its 10ft default whenever GPS hadn't (yet)
  // confirmed the player was on the green, which is exactly when a ball gets
  // tapped by hand. Confirmed on course, Brentwood hole 1: ~28ft putt on the
  // diagram, app showed 10ft and read the break for 10ft.
  useEffect(() => {
    if (puttBallToPinDistanceFt != null && puttBallToPinDistanceFt > 0) {
      setPuttDistanceFt(Math.round(puttBallToPinDistanceFt));
    } else if (isOnGreenLikely && autoTargetYardage != null && autoTargetYardage > 0) {
      setPuttDistanceFt(Math.round(autoTargetYardage * 3));
    }
  }, [isOnGreenLikely, puttBallToPinDistanceFt, autoTargetYardage]);

  if (showPinSheetSetup) {
    return (
      <PinSheetSetup
        courseId={courseId}
        onClose={() => setShowPinSheetSetup(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 pb-24">
      {/* Sticky header + tab bar */}
      <div className="sticky top-0 z-20 bg-slate-950 border-b border-slate-800 px-4 pt-3 pb-0">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-base font-bold tracking-tight">Sweet Shot Select</h1>
          <span className="text-xs text-slate-400">Hole {hole} · {playerId}</span>
        </div>
        <div className="flex gap-1">
          {(["shot", "putt", "profile", "help"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-emerald-500 text-emerald-400 bg-slate-900"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              {tab === "shot" ? "Shot" : tab === "putt" ? "Putt" : tab === "profile" ? "Profile" : "Help"}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

        {/* ── SHOT TAB ── */}
        {activeTab === "shot" && (
          <div className="space-y-4">
            {/* Hole + pin row */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  Hole
                  <select
                    value={hole}
                    onChange={(e) => handleHoleChange(Number(e.target.value) || 1)}
                    className="rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-base font-bold text-white"
                  >
                    {Array.from({ length: 18 }).map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </label>
                {autoAdvanceNotice && (
                  <span className="text-xs text-emerald-400">{autoAdvanceNotice}</span>
                )}

                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-400">Target</label>
                  <select
                    value={targetType}
                    onChange={(e) => setTargetType(e.target.value as TargetType)}
                    className="rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  >
                    <option value="pin">Pin</option>
                    <option value="center">Center</option>
                    <option value="layup">Layup</option>
                  </select>
                  {targetType === "pin" && (
                    <span
                      className={pinSheet[hole] ? "text-xs text-emerald-400" : "text-xs text-slate-500"}
                      title={pinSheet[hole] ? "Using today's pin sheet position" : "Using front/middle/back estimate — no pin sheet position set"}
                    >
                      {pinSheet[hole] ? "sheet" : "est."}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1 text-xs text-slate-400">
                  GPS:
                  <span
                    className={
                      playerLat == null
                        ? "text-slate-500"
                        : lastGpsAccuracyM != null && lastGpsAccuracyM > GPS_ACCURACY_THRESHOLD_M
                        ? "text-amber-400"
                        : "text-emerald-400"
                    }
                    title={
                      lastGpsAccuracyM != null && lastGpsAccuracyM > GPS_ACCURACY_THRESHOLD_M
                        ? "GPS accuracy degraded — compare against the cart's number"
                        : undefined
                    }
                  >
                    {playerLat != null
                      ? `${lastGpsAccuracyM != null ? Math.round(lastGpsAccuracyM) + "m" : "active"}${
                          lastGpsAccuracyM != null && lastGpsAccuracyM > GPS_ACCURACY_THRESHOLD_M ? " ⚠" : ""
                        }`
                      : "off"}
                  </span>
                </div>
              </div>

              {/* Pin location */}
              <div>
                <div className="text-xs text-slate-400 mb-1">Pin</div>
                <div className="flex gap-2 mb-1">
                  {(["left", "center", "right"] as const).map((val) => (
                    <button key={val} type="button"
                      onClick={() => setPinLateralThird(val)}
                      className={`flex-1 py-1 rounded text-xs border font-semibold ${pinLateralThird === val ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                    >{val}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  {(["front", "middle", "back"] as const).map((val) => (
                    <button key={val} type="button"
                      onClick={() => setPinDepthThird(val)}
                      className={`flex-1 py-1 rounded text-xs border font-semibold ${pinDepthThird === val ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                    >{val}</button>
                  ))}
                </div>
              </div>
            </section>

            {/* Conditions */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-slate-200">Conditions</h2>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {/* Yardage */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Yardage</label>
                  <input
                    type="number" inputMode="decimal"
                    value={displayNumber(inputs.rawYardage)}
                    onChange={(e) => {
                      handleShotInputChange("rawYardage", e.target.value === "" ? 0 : Number(e.target.value));
                      setYardageSource("manual");
                      setLastYardageTouchedAt(Date.now());
                    }}
                    className="w-full px-2 py-2 border border-slate-400 rounded bg-white text-slate-900 text-sm font-semibold"
                  />
                  {inputs.rawYardage <= 0 && targetType !== "layup" && (
                    <div className="mt-1 text-xs text-red-400">⚠ Enter yardage</div>
                  )}
                  {gpsYardageUsable && (
                    <button type="button"
                      onClick={() => { handleShotInputChange("rawYardage", Math.round(autoTargetYardage)); setYardageSource("auto"); }}
                      className="mt-1 w-full px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 text-xs font-semibold"
                    >GPS: {autoTargetYardage.toFixed(0)} yds</button>
                  )}
                  {yardageLooksStale && (
                    <div className="mt-1 text-xs text-yellow-400">GPS differs by {yardageDeltaFromGps?.toFixed(0)} yds</div>
                  )}
                </div>

                {/* Wind */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Wind (mph)</label>
                  <input type="number" inputMode="decimal"
                    className="w-full px-2 py-2 border border-slate-400 rounded bg-white text-slate-900 text-sm font-semibold"
                    value={displayNumber(inputs.windSpeedMph)}
                    onChange={handleNumericChange("windSpeedMph")}
                  />
                  {windGustMph != null && (
                    <div className="text-xs text-yellow-400 mt-1">Gusts: {Math.round(windGustMph)} mph</div>
                  )}
                  <div className={`mt-1 text-xs font-semibold ${
                    windBodyBucketDisplay === "helping" ? "text-emerald-400" :
                    windBodyBucketDisplay === "hurting" ? "text-red-400" : "text-yellow-300"
                  }`}>{windBodyBucketDisplay}</div>
                </div>

                {/* Slope */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Slope</label>
                  <div className="flex gap-1">
                    <select value={slopeDirection}
                      onChange={(e) => setSlopeDirection(e.target.value as "level" | "uphill" | "downhill")}
                      className="flex-1 rounded bg-slate-900 border border-slate-600 px-1 py-1 text-xs text-white"
                    >
                      <option value="level">Level</option>
                      <option value="uphill">Up</option>
                      <option value="downhill">Down</option>
                    </select>
                    <input type="number" inputMode="decimal" min={0} step="1"
                      value={displayNumber(slopeMagnitude)}
                      onChange={(e) => setSlopeMagnitude(e.target.value === "" ? 0 : Number(e.target.value))}
                      placeholder="yds"
                      className="w-16 px-2 py-1 border border-slate-400 rounded bg-white text-slate-900 text-xs"
                    />
                  </div>
                  {elevationSource === "gps" && (
                    <div className="text-xs text-emerald-400 mt-1">
                      GPS {elevationDeltaFt != null ? `${elevationDeltaFt > 0 ? "+" : ""}${elevationDeltaFt.toFixed(1)}ft` : ""}
                    </div>
                  )}
                  {elevationSource === "fallback" && (
                    <div className="text-xs text-yellow-400 mt-1">Set manually</div>
                  )}
                </div>

                {/* Lie */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Lie</label>
                  <select value={inputs.lieType} onChange={handleLieTypeChange}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-2 text-xs text-white"
                  >
                    <option value="fairway">Fairway</option>
                    <option value="tee">Tee</option>
                    <option value="rough-light">Light rough</option>
                    <option value="rough-heavy">Heavy rough</option>
                  </select>
                </div>

                {/* Wind direction */}
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Wind direction</label>
                  <div className="flex gap-2 mb-2">
                    <button type="button" onClick={() => setWindDirectionMode("live")}
                      className={`px-3 py-1 rounded text-xs font-semibold border ${windDirectionMode === "live" ? "bg-emerald-600 text-white border-emerald-700" : "bg-slate-700 text-slate-200 border-slate-500"}`}
                    >Live</button>
                    <button type="button" onClick={() => {
                      setWindDirectionMode("manual");
                      const snap = Math.round(normalizeDeg360((Number(inputs.windDirectionDeg) || 0) - effectiveShotAzimuthDeg) / 45) * 45 % 360;
                      setManualRelativeWindBodyDeg(snap);
                    }}
                      className={`px-3 py-1 rounded text-xs font-semibold border ${windDirectionMode === "manual" ? "bg-sky-600 text-white border-sky-700" : "bg-slate-700 text-slate-200 border-slate-500"}`}
                    >Manual</button>
                  </div>
                  <select value={manualRelativeWindBodyDeg}
                    onChange={(e) => { if (windDirectionMode === "manual") setManualRelativeWindBodyDeg(Number(e.target.value)); }}
                    disabled={windDirectionMode !== "manual"}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                  >
                    {WIND_DIRECTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Temp + Humidity */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Temp (°F)</label>
                  <input type="number" inputMode="decimal"
                    className="w-full px-2 py-1 border border-slate-400 rounded bg-white text-slate-900 text-xs"
                    value={displayNumber(inputs.temperatureF)}
                    onChange={handleNumericChange("temperatureF")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Humidity (%)</label>
                  <input type="number" inputMode="decimal"
                    className="w-full px-2 py-1 border border-slate-400 rounded bg-white text-slate-900 text-xs"
                    value={displayNumber(inputs.humidityPct)}
                    onChange={handleNumericChange("humidityPct")}
                  />
                </div>

                {/* Energy */}
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">
                    Energy {inputs.energyPct}%
                  </label>
                  <input type="range" min={0} max={100} value={inputs.energyPct}
                    onChange={(e) => handleShotInputChange("energyPct", Number(e.target.value) || 0)}
                    className="w-full"
                  />
                </div>
              </div>

              {/* GPS/Weather buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700">
                <button type="button" onClick={handleRefreshLocation}
                  className="px-3 py-1 rounded bg-sky-700 hover:bg-sky-600 text-xs font-semibold"
                >{playerLat == null ? "Get location" : "Refresh GPS"}</button>
                <button type="button" onClick={handleLoadWeather}
                  className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-xs font-semibold"
                >Weather</button>
                <button type="button" onClick={handleLoadElevation}
                  className="px-3 py-1 rounded bg-violet-700 hover:bg-violet-600 text-xs font-semibold"
                >Elevation</button>
              </div>
              <div className="text-[11px] text-slate-500 space-y-0.5">
                <div>GPS: {gpsStatus} · {gpsFixAgeMs == null ? "no fix" : `${Math.round(gpsFixAgeMs/1000)}s ago`} · {lastGpsAccuracyM != null ? Math.round(lastGpsAccuracyM)+"m" : "accuracy unknown"}</div>
                {weatherStatus !== "idle" && <div>Weather: {weatherStatus}{weatherError ? ` — ${weatherError}` : ""}</div>}
                {elevationStatus !== "idle" && <div>Elevation: {elevationStatus}{elevationError ? ` — ${elevationError}` : ""}</div>}
                {gpsError && <div className="text-red-400">GPS error: {gpsError}</div>}
              </div>
            </section>

            {/* Hazards — collapsible */}
            <details className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
              <summary className="text-sm font-semibold text-slate-200 cursor-pointer select-none">Hazards</summary>
              <div className="mt-3 space-y-2">
                {([["Left", leftPenalty, setLeftPenalty], ["Right", rightPenalty, setRightPenalty], ["Front", frontPenalty, setFrontPenalty], ["Back", backPenalty, setBackPenalty]] as const).map(([label, value, setter]) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="w-10 text-xs text-slate-400">{label}</span>
                    {(["none", "near", "adjacent"] as const).map((opt) => (
                      <button key={opt} type="button" onClick={() => setter(opt)}
                        className={`px-2 py-0.5 rounded text-xs border ${value === opt ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                      >{opt}</button>
                    ))}
                  </div>
                ))}
              </div>
            </details>

            {/* Calculate button */}
            <button type="button" onClick={handleCalculateShot}
              className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-lg font-bold tracking-wide shadow-lg"
            >
              Calculate Shot
            </button>

            {/* Recommendation */}
            {lastRec ? (
              <section className="bg-slate-800/60 border border-emerald-700 rounded-xl p-4 space-y-3">
                {/* Club headline */}
                <div className="text-center py-2">
                  <div className="text-4xl font-black text-emerald-400">{lastRec.club}</div>
                  <div className="text-sm text-slate-300 mt-1">
                    {lastRec.clubCarryYards != null ? `${lastRec.clubCarryYards} yds carry · ` : ""}
                    plays like <span className="font-semibold text-white">{Math.round(lastRec.playsLikeYards)} yds</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {targetType !== "layup" ? `Expected ~${lastRec.expectedProximityFt.toFixed(1)} ft from hole` : ""}
                    {" · "}confidence: {lastRec.confidence}
                  </div>
                </div>

                {/* Landing target */}
                {targetRec && targetType !== "layup" && (() => {
                  const lat = typeof targetRec.targetLateralYds === "number" && Number.isFinite(targetRec.targetLateralYds) ? targetRec.targetLateralYds : 0;
                  const lng = typeof targetRec.targetDepthYds === "number" && Number.isFinite(targetRec.targetDepthYds) ? targetRec.targetDepthYds : 0;
                  return (
                    <div className="bg-slate-900/60 rounded-lg p-3 text-sm text-slate-200">
                      <span className="font-semibold text-slate-100">Target: </span>
                      {lat === 0 ? "on line with pin" : `${Math.abs(lat)} yds ${lat > 0 ? "right" : "left"} of pin`}
                      {lng !== 0 && ` · favor ${lng > 0 ? "long" : "short"} ${Math.abs(lng)} yds`}
                    </div>
                  );
                })()}

                {/* Why */}
                <details className="text-xs text-slate-400">
                  <summary className="cursor-pointer text-slate-300 font-semibold">Why this club?</summary>
                  <p className="mt-2 leading-relaxed">{lastRec.explanation}</p>
                </details>

                {/* Outcome capture — prominent */}
                {lastShotOutcomeKey && (
                  <div className="border-t border-slate-700 pt-3 space-y-3">
                    <div className="text-sm font-semibold text-slate-200">How’d it go?</div>

                    <div>
                      <div className="text-xs text-slate-400 mb-1">Shot execution</div>
                      <div className="flex gap-2">
                        {([["solid", "Solid"], ["cant_judge", "Can’t judge"]] as const).map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setShotExecution(v)}
                            className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${shotExecution === v ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                          >{label}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400 mb-1">Rec quality</div>
                      <div className="flex gap-2">
                        {([["agree", "Agree"], ["dont_agree", "No"], ["unclear", "Unclear"]] as const).map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setRecommendationQuality(v)}
                            className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${recommendationQuality === v ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                          >{label}</button>
                        ))}
                      </div>
                    </div>

                    <button type="button" onClick={handleSaveShotOutcome}
                      disabled={outcomeStatus === "saving"}
                      className="w-full py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-sm font-semibold disabled:opacity-60"
                    >
                      {outcomeStatus === "saving" ? "Saving…" : outcomeStatus === "saved" ? "Saved ✓" : "Save outcome"}
                    </button>
                    {outcomeStatus === "error" && <div className="text-xs text-red-400">{outcomeError}</div>}
                  </div>
                )}

                {/* Next actions */}
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={handleNextShot}
                    className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-sm font-semibold"
                  >Next Shot</button>
                  <button type="button" onClick={handleNextHole}
                    className="flex-1 py-3 rounded-xl bg-sky-700 hover:bg-sky-600 text-sm font-semibold"
                  >Next Hole {hole < 18 ? `→ ${hole + 1}` : "(done)"}</button>
                </div>
              </section>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">
                Enter conditions above and tap <span className="font-semibold text-slate-200">Calculate Shot</span>.
              </p>
            )}

            {/* Wind debug — collapsed */}
            <details className="text-[11px] text-slate-500">
              <summary className="cursor-pointer text-slate-400">Wind / azimuth debug</summary>
              <div className="mt-2 space-y-0.5 font-mono">
                <div>Shot az: {effectiveShotAzimuthDisplay.toFixed(1)}° ({shotAzimuthDeg != null ? "GPS" : "fallback"})</div>
                <div>Wind from: {rawWindDegDisplay.toFixed(1)}° → to: {rawWindToDegDisplay.toFixed(1)}°</div>
                <div>Relative (vector): {relativeWindDegDisplay.toFixed(1)}°</div>
                <div>Relative (body): {relativeWindFromDegDisplay.toFixed(1)}°</div>
                <div>Effect: {windBodyBucketDisplay}</div>
                <div>Player: {playerLat?.toFixed(5) ?? "—"}, {playerLng?.toFixed(5) ?? "—"}</div>
                <div>Target: {targetLat?.toFixed(5) ?? "—"}, {targetLng?.toFixed(5) ?? "—"}</div>
                {distanceToGreenCenterYds != null && <div>Green center: {distanceToGreenCenterYds.toFixed(1)} yds · near:{isNearGreen?"y":"n"} · on:{isOnGreenLikely?"y":"n"}</div>}
              </div>
              <WindCompassOverlay
                rawRelativeWindDeg={relativeWindDegDisplay}
                adjustedRelativeWindDeg={relativeWindDegDisplay + 5}
                windBucket={windBucketDisplay}
              />
              <WindAnalytics courseId={courseId} hole={hole} />
            </details>
          </div>
        )}

        {/* ── PUTT TAB ── */}
        {activeTab === "putt" && (
          <div className="space-y-4">

            {/* Stimp banner — set once per round */}
            <div className="flex items-center gap-3 bg-slate-900 border border-slate-700 rounded-xl px-4 py-2">
              <span className="text-xs text-slate-400 flex-1">Green speed (Stimp)</span>
              <div className="flex items-center gap-2">
                {[9, 10, 11, 12, 13].map(s => (
                  <button key={s} type="button"
                    onClick={() => setGreenStimp(s)}
                    className={`w-8 h-8 rounded-full text-xs font-bold border transition-colors ${
                      greenStimp === s
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            {/* ── PUTT COUNTER ── */}
            <div className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-2 gap-2">
              <span className="text-sm font-semibold text-slate-200 shrink-0">Putt {puttNumber}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleNextPutt}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-500 text-white"
                >Next putt →</button>
                <button
                  type="button"
                  onClick={handleNextHole}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sky-700 hover:bg-sky-600 text-white"
                >Next Hole {hole < 18 ? `→ ${hole + 1}` : "(done)"}</button>
              </div>
            </div>

            {/* ── LARGE READ RESULT ── */}
            <section className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-600 rounded-2xl p-5 space-y-3">
              {/* Distance — kept with the aim/break numbers it's read
                  alongside, not off-screen in a separate section. */}
              <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                <label className="text-base text-slate-400 shrink-0">
                  Distance (ft)
                  {puttBallToPinDistanceFt != null && <span className="block text-emerald-400 text-sm font-semibold">~{Math.round(puttBallToPinDistanceFt)} ft</span>}
                  {isOnGreenLikely && puttBallToPinDistanceFt == null && autoTargetYardage != null && <span className="block text-emerald-400 text-sm font-semibold">GPS ~{Math.round(autoTargetYardage * 3)} ft</span>}
                </label>
                <input type="number" step="1" value={puttDistanceFt}
                  onChange={(e) => setPuttDistanceFt(Number(e.target.value) || 0)}
                  className="w-28 rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-4xl font-black text-white text-center"
                />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-3xl font-black text-white leading-tight">{puttRead.aimInstruction}</div>
                  <div className="text-base font-semibold text-emerald-400 mt-1">{puttRead.readInstruction}</div>
                </div>
                {puttRead.breakInches > 0.5 && (
                  <div className="text-right">
                    <div className="text-4xl font-black text-emerald-400">{puttRead.breakInches.toFixed(1)}<span className="text-lg font-normal text-slate-400">"</span></div>
                    <div className="text-xs text-slate-500">break</div>
                  </div>
                )}
              </div>
              <div className="border-t border-slate-700 pt-3 text-lg text-slate-300 font-medium">{puttRead.speedInstruction}</div>
              <div className="text-base text-slate-400">{puttRead.gradeDescription}</div>
              {puttRead.multiBreakNote && (
                <div className="text-xs text-amber-400 border-t border-slate-700 pt-2">{puttRead.multiBreakNote}</div>
              )}
              {puttRead.readMethod === "path" ? (
                <div className="text-xs text-emerald-500">
                  Auto read &middot; {puttRead.pathSamplesUsed} points along the line &middot; Tap pin on image below
                </div>
              ) : greenVectors ? (
                <div className="text-xs text-slate-500">
                  {greenVectors.length} slope vectors loaded, but none with a usable direction near this line &middot; using manual inputs below
                </div>
              ) : null}

              {/* Outcome capture — mirrors the shot recommendation's, so the
                  putt read has real outcome data to check itself against. */}
              <div className="border-t border-slate-700 pt-3 space-y-3">
                <div className="text-sm font-semibold text-slate-200">How’d it go?</div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">Holed it?</div>
                  <div className="flex gap-2">
                    {([["yes", "Yes"], ["no", "No"]] as const).map(([v, label]) => (
                      <button key={v} type="button" onClick={() => setPuttHoled(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${puttHoled === v ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                      >{label}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">Read accuracy</div>
                  <div className="flex gap-2">
                    {([["agree", "Agree"], ["dont_agree", "No"], ["unclear", "Unclear"]] as const).map(([v, label]) => (
                      <button key={v} type="button" onClick={() => setPuttReadQuality(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${puttReadQuality === v ? "bg-blue-600 text-white border-blue-700" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                      >{label}</button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={handleSavePuttOutcome}
                  disabled={puttOutcomeStatus === "saving"}
                  className="w-full py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-sm font-semibold disabled:opacity-60"
                >
                  {puttOutcomeStatus === "saving" ? "Saving…" : puttOutcomeStatus === "saved" ? "Saved ✓" : "Save putt read"}
                </button>
                {puttOutcomeStatus === "error" && <div className="text-xs text-red-400">{puttOutcomeError}</div>}
              </div>
            </section>

            {/* ── KEY INPUTS ──
                Side of fall line / above-below hole — only needed as manual
                input when the pin position itself isn't already known (no
                pin sheet entry, no tap yet). Once the pin is known, these
                toggles just add confusing, unused controls to the screen,
                even on the rare line where the auto read still falls back
                to manual slope inputs for lack of nearby vector data. */}
            {!pinKnown && (
              <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Ball is… (relative to fall line)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(["left", "right"] as const).map(side => (
                      <button key={side} type="button"
                        onClick={() => setSideOfFallLine(side)}
                        className={`py-3 rounded-xl text-sm font-bold border transition-colors ${
                          sideOfFallLine === side
                            ? "bg-blue-600 border-blue-500 text-white"
                            : "bg-slate-700 border-slate-600 text-slate-300"
                        }`}
                      >{side === "left" ? "⬅ Left of fall line" : "Right of fall line ➡"}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-400 mb-2">Ball is… (vs. hole elevation)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(["above", "below"] as const).map(pos => (
                      <button key={pos} type="button"
                        onClick={() => setHeightVsHole(pos as BallHeightVsHole)}
                        className={`py-3 rounded-xl text-sm font-bold border transition-colors ${
                          heightVsHole === pos
                            ? "bg-blue-600 border-blue-500 text-white"
                            : "bg-slate-700 border-slate-600 text-slate-300"
                        }`}
                      >{pos === "above" ? "⬆ Above hole" : "⬇ Below hole"}</button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Advanced — collapsed */}
            <details className="bg-slate-800/40 border border-slate-700 rounded-xl px-4 py-2 text-xs">
              <summary className="cursor-pointer text-slate-400 font-semibold py-1">Advanced inputs</summary>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Start slope (°)</label>
                  <input type="number" step="0.1" value={thetaStartDeg}
                    onChange={(e) => setThetaStartDeg(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Mid slope (°)</label>
                  <input type="number" step="0.1" value={thetaMidDeg}
                    onChange={(e) => setThetaMidDeg(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Apex slope (°)</label>
                  <input type="number" step="0.1" value={thetaApexDeg}
                    onChange={(e) => setThetaApexDeg(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Rollout (ft)</label>
                  <input type="number" step="0.1" value={rolloutFt}
                    onChange={(e) => setRolloutFt(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">X from fall line (ft)</label>
                  <input type="number" step="0.5" value={fallLineXFt}
                    onChange={(e) => setFallLineXFt(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Y from hole (ft)</label>
                  <input type="number" step="0.5" value={fallLineYFt}
                    onChange={(e) => setFallLineYFt(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Terrain bias (°)</label>
                  <input type="number" step="0.1" value={mtbDeg}
                    onChange={(e) => setMtbDeg(Number(e.target.value) || 0)}
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white"
                  />
                </div>
              </div>
              <div className="mt-3 text-slate-500 space-y-0.5">
                <div>Coord read: {fallLineCoordinateRead.breakDirection.replace(/-/g, " ")} · grade: {fallLineCoordinateRead.puttGrade}</div>
              </div>
            </details>

            {/* Green image — tap to set ball and/or pin position */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
              {(() => {
                const sheetPin = pinSheet[hole];
                // Marker position always comes from resolvedPinNorm (same
                // vectorizer-aligned frame the putt engine uses) — never
                // sheetPin's own xNorm/yNorm, which is tapped against a
                // different, full-page image.
                const pinXNorm = resolvedPinNorm?.xNorm ?? null;
                const pinYNorm = resolvedPinNorm?.yNorm ?? null;
                // Once a pin position is known (tapped or preloaded from
                // today's pin sheet), the only thing left to tap is the
                // ball — no mode toggle needed. Otherwise let the golfer
                // choose which one they're placing.
                const activeMode: "pin" | "ball" = pinKnown ? "ball" : greenTapMode;

                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-sm font-semibold text-slate-200">Green layout — Hole {hole}</h2>
                      <div className="flex items-center gap-3">
                        {tappedBallXNorm !== null && (
                          <button
                            type="button"
                            onClick={() => { setTappedBallXNorm(null); setTappedBallYNorm(null); }}
                            className="text-xs text-slate-400 underline"
                          >Clear ball</button>
                        )}
                        {tappedPinXNorm !== null && (
                          <button
                            type="button"
                            onClick={() => { setTappedPinXNorm(null); setTappedPinYNorm(null); }}
                            className="text-xs text-slate-400 underline"
                          >Clear pin</button>
                        )}
                      </div>
                    </div>

                    {!pinKnown && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-slate-400">Tapping sets:</span>
                        <div className="inline-flex rounded-lg border border-slate-600 overflow-hidden">
                          {(["pin", "ball"] as const).map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setGreenTapMode(m)}
                              className={`px-3 py-1 text-xs ${greenTapMode === m ? "bg-emerald-600 text-white" : "bg-slate-900 text-slate-300"}`}
                            >{m === "pin" ? "Pin" : "Ball"}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {pinKnown && sheetPin && tappedPinXNorm === null && (
                      <p className="text-xs text-emerald-400 mb-2">Using today's pin sheet position · tap the image below for your ball</p>
                    )}
                    {greenLayoutLoading && <p className="text-xs text-slate-400">Loading…</p>}
                    {!greenLayoutLoading && greenLayoutError && <p className="text-xs text-red-400">{greenLayoutError}</p>}
                    {!vectorTapImageUrl && greenLayoutUrl && (
                      <p className="text-xs text-amber-400 mb-2">
                        No vector-aligned image for this hole yet — taps below use the full page photo, which won't line up precisely with slope data.
                      </p>
                    )}
                    {!greenLayoutLoading && !greenLayoutError && (vectorTapImageUrl ?? greenLayoutUrl) && (
                      <div
                        className="relative w-full max-w-md mx-auto cursor-crosshair select-none"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const rawX = (e.clientX - rect.left) / rect.width;
                          const rawY = (e.clientY - rect.top) / rect.height;
                          // vectorTapImageUrl is padded with margin beyond the
                          // green's real half-dimensions (see
                          // tapImageMarginRatios) -- greenLayoutUrl (the
                          // full-page-photo fallback) is not, and uses a
                          // completely different frame already.
                          const x = Math.max(0, Math.min(1, vectorTapImageUrl ? imageFracToXNorm(rawX, tapImageMarginRatios.x) : rawX));
                          const y = Math.max(0, Math.min(1, vectorTapImageUrl ? imageFracToXNorm(rawY, tapImageMarginRatios.y) : rawY));
                          if (activeMode === "ball") {
                            setTappedBallXNorm(x);
                            setTappedBallYNorm(y);
                            lastBallTapAtRef.current = Date.now();
                          } else {
                            setTappedPinXNorm(x);
                            setTappedPinYNorm(y);
                          }
                        }}
                      >
                        <img
                          src={(vectorTapImageUrl ?? greenLayoutUrl) as string}
                          alt={`Green layout hole ${hole}`}
                          className="w-full rounded shadow pointer-events-none"
                          draggable={false}
                        />
                        {/* Pin marker — only once a real (tapped or pin-sheet) position is known, not for the rough 9-zone guess */}
                        {pinKnown && pinXNorm !== null && pinYNorm !== null && (
                          <div
                            className="absolute pointer-events-none"
                            style={{
                              left: `${(vectorTapImageUrl ? xNormToImageFrac(pinXNorm, tapImageMarginRatios.x) : pinXNorm) * 100}%`,
                              top: `${(vectorTapImageUrl ? xNormToImageFrac(pinYNorm, tapImageMarginRatios.y) : pinYNorm) * 100}%`,
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            <div className="w-5 h-5 rounded-full border-2 border-yellow-400 bg-yellow-400/30 flex items-center justify-center">
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                            </div>
                          </div>
                        )}
                        {/* Ball marker */}
                        {tappedBallXNorm !== null && tappedBallYNorm !== null && (
                          <div
                            className="absolute pointer-events-none"
                            style={{
                              left: `${(vectorTapImageUrl ? xNormToImageFrac(tappedBallXNorm, tapImageMarginRatios.x) : tappedBallXNorm) * 100}%`,
                              top: `${(vectorTapImageUrl ? xNormToImageFrac(tappedBallYNorm, tapImageMarginRatios.y) : tappedBallYNorm) * 100}%`,
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            <div className="w-4 h-4 rounded-full border-2 border-white bg-white/40" />
                          </div>
                        )}
                        {/* Hint */}
                        {((activeMode === "pin" && !pinKnown) || (activeMode === "ball" && tappedBallXNorm === null)) && (
                          <div className="absolute inset-0 flex items-end justify-center pb-3 pointer-events-none">
                            <span className="text-xs text-white/70 bg-black/50 px-2 py-1 rounded">
                              Tap to set {activeMode === "ball" ? "your ball position" : "pin"}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {!greenLayoutLoading && !greenLayoutError && !vectorTapImageUrl && !greenLayoutUrl && (
                      <p className="text-xs text-slate-400">No image saved for this hole.</p>
                    )}
                  </>
                );
              })()}
            </section>
          </div>
        )}

        {/* ── PROFILE TAB ── */}
        {activeTab === "profile" && (
          <div className="space-y-4">

            {/* Course selector */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
              <h2 className="text-sm font-semibold text-slate-200">Course</h2>
              <select
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-2 text-sm text-slate-100"
              >
                <option value="BrentwoodCC">Brentwood Country Club</option>
                <option value="WoodlandHillsCC">Woodland Hills Country Club</option>
                <option value="WaccabucCC">Waccabuc Country Club</option>
              </select>
              <p className="text-xs text-slate-500">Green layouts and slope vectors load per course. Switch courses before your round.</p>
              <label className="flex items-center gap-2 text-xs text-slate-300 pt-1">
                <input
                  type="checkbox"
                  checked={autoAdvanceHole}
                  onChange={(e) => setAutoAdvanceHole(e.target.checked)}
                />
                Auto-advance to next hole after leaving the green
              </label>
            </section>

            {/* Text size */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
              <h2 className="text-sm font-semibold text-slate-200">👓 Text size</h2>
              <div className="flex gap-2">
                {TEXT_SCALE_OPTIONS.map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setTextScalePct(pct)}
                    className={`flex-1 py-2 rounded border font-semibold ${
                      textScalePct === pct
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-slate-900 text-slate-300 border-slate-600"
                    }`}
                    style={{ fontSize: `${pct}%` }}
                  >
                    {pct === 100 ? "Normal" : pct === 115 ? "Large" : "X-Large"}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Resizes text throughout the app. Sticks between sessions.</p>
            </section>

            {/* Pin sheet setup */}
            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
              <h2 className="text-sm font-semibold text-slate-200">Pin sheet</h2>
              <p className="text-xs text-slate-500">
                {Object.keys(pinSheet).length}/18 pins set for {courseId}.
                Load today's pin positions once before you tee off, so the
                Shot tab uses the real pin instead of a front/middle/back
                estimate.
              </p>
              <button
                type="button"
                onClick={() => setShowPinSheetSetup(true)}
                className="w-full py-2 text-sm rounded bg-emerald-700 hover:bg-emerald-600 font-semibold"
              >Set today's pins</button>
            </section>

            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-slate-200">Player</h2>

              <div className="flex flex-wrap gap-2 items-center">
                <input type="text" value={playerIdInput}
                  onChange={(e) => setPlayerIdInput(e.target.value)}
                  placeholder="Player ID"
                  className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-1 text-sm text-white"
                />
                <button type="button" onClick={handleLoadSwitchPlayer}
                  className="px-3 py-1 text-xs rounded border border-slate-500 bg-slate-900 hover:bg-slate-800"
                >Switch</button>
                <button type="button" onClick={handleSaveProfileBag} disabled={saving}
                  className="px-3 py-1 text-xs rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60"
                >{saving ? "Saving…" : "Save"}</button>
              </div>

              {saveStatus === "error" && saveError && <div className="text-xs text-red-300">{saveError}</div>}
              {saveStatus === "saved" && <div className="text-xs text-emerald-300">Saved.</div>}

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-24 text-xs text-slate-400">Name</span>
                  <input type="text" value={playerProfile.name}
                    onChange={(e) => setPlayerProfile((p) => ({ ...p, name: e.target.value }))}
                    className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-1 text-sm text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-24 text-xs text-slate-400">Handicap</span>
                  <input type="number" inputMode="decimal" value={displayNumber(playerProfile.handicap)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      const handicap = Number.isFinite(v) ? v : 10;
                      setPlayerProfile((p) => ({ ...p, handicap }));
                      setInputs((p) => ({ ...p, handicap }));
                    }}
                    className="w-20 px-2 py-1 border border-slate-600 rounded bg-slate-900 text-sm text-white"
                  />
                  <span className="text-xs text-slate-400">Index</span>
                  <input type="number" inputMode="decimal" value={playerProfile.handicapIndex ?? ""}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setPlayerProfile((p) => ({ ...p, handicapIndex: Number.isFinite(v) ? v : undefined }));
                    }}
                    className="w-20 px-2 py-1 border border-slate-600 rounded bg-slate-900 text-sm text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-24 text-xs text-slate-400">Altitude (ft)</span>
                  <input type="number" inputMode="numeric" value={playerProfile.altitudeFt ?? ""}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setPlayerProfile((p) => ({ ...p, altitudeFt: Number.isFinite(v) ? v : undefined }));
                    }}
                    className="w-28 px-2 py-1 border border-slate-600 rounded bg-slate-900 text-sm text-white"
                  />
                </div>
              </div>
            </section>

            <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
              <h2 className="text-sm font-semibold text-slate-200 mb-3">Bag ({currentBag.length} clubs)</h2>
              <p className="text-xs text-slate-400 mb-2">Edit carry distances. Tap Save above when done.</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-1 pr-4 text-xs text-slate-400">Club</th>
                      <th className="text-left py-1 text-xs text-slate-400">Carry (yds)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBag.map((club, index) => (
                      <tr key={`${club.name}-${index}`} className="border-b border-slate-700/60">
                        <td className="py-1 pr-4 text-slate-50 text-xs">{club.name}</td>
                        <td className="py-1">
                          <input type="number" inputMode="numeric"
                            value={Number.isFinite(club.carryYards) ? club.carryYards : ""}
                            onChange={handleBagCarryChange(index)}
                            className="w-20 px-2 py-0.5 border border-slate-600 rounded bg-slate-900 text-xs text-white"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}

        {/* ── HELP TAB ── */}
        {activeTab === "help" && <HelpTab />}

      </div>
    </div>
  );
};

  export default ShotPlanner;